import { DatePipe } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, DestroyRef, ElementRef, inject, Input, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CalendarMonthViewDay } from 'angular-calendar';
import moment from 'moment';
import { finalize } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { DATE_ISO_FORMAT, DATE_TIME_ISO_T_FORMAT } from '../../../constants/date.constants';
import { EVENT_CONSTANT } from '../../../constants/event-constants';
import { TRANSLATION_TEMPLATES } from '../../../constants/translation-templates-constants';
import { APPOINTMENT_SOURCE_ID, BOOKING_CUSTOM_FIELD_TYPES, BOOKING_REDIRECTION_VIEWS, CAPTCHA_TYPE, EVENT_PAGINATION_LIMIT, GTM_EVENTS, PAYMENT_TYPES, WIDGET_CONSTANTS, WIDGET_DISPLAY_TIMEZONE_SOURCE, WINDOW_LISTENER_CONSTANTS } from '../../../constants/widget-constants';
import { CustomerDbModel, CustomFieldOptions, CustomFieldsDao } from '../../../db-models/appointment-custom-fields-dao';
import { CouponDbModel } from '../../../db-models/coupon-db.model';
import { EventDao, EventPaginationDao } from '../../../db-models/event-dao';
import { EventDateDao } from '../../../db-models/event-date-dao';
import { PayPalOrderDetailModel, PayPalSettingsDbModel } from '../../../db-models/partner-setting.model';
import { TimezoneDbModel } from '../../../db-models/timezone.model';
import { CustomBookingMessageTemplate, EventsAnalyticsDao, WidgetBookingInfoDao } from '../../../db-models/widget-conf-dao';
import { EventBookItem, EventCartItem, GuestModel } from '../../../models/cart.model';
import { GlobalObjects, Partner } from '../../../models/global';
import { AdyenPaymentDetails } from '../../../models/adyen.model';
import { AdyenResumeFailure, AdyenResumeReturn, AdyenResumeSnapshot } from '../../../models/adyen-resume.model';
import { AdyenResumeService } from '../../../services/adyen-resume.service';
import { buildAdyenHoldScope } from '../../../services/adyen-booking-context.util';
import { canBookAfterAdyenPayment } from '../../../services/adyen-payment-booking.util';
import { SaferPayPaymentDetailsDbModel } from '../../../models/saferpay.model';
import { EventState, UserState } from '../../../models/state.model';
import { WidgetColorConf } from '../../../models/widget-color.model';
import { CustomerPrefillDataModel } from '../../../models/widget-conf';
import { CaptchaService } from '../../../services/captcha.service';
import { CustomEventService } from '../../../services/custom-event.service';
import { CustomFieldService } from '../../../services/custom-field.service';
import { EventCartService } from '../../../services/event-cart.service';
import { EventsHelperService } from '../../../services/events-helper.service';
import { FormsService } from '../../../services/forms.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { HelperService } from '../../../services/helper.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { LoggerService } from '../../../services/logger.service';
import { PartnerService } from '../../../services/partner.service';
import { PaymentService } from '../../../services/payment.service';
import { TimezoneService } from '../../../services/timezone.service';
import { UtilService } from '../../../services/util.service';
import { calculateVat, isVatAddedOnTop, VatResult } from '../../../services/vat-calculation.util';
import { WidgetUtilService } from '../../../services/widget-util.service';
import { AlertComponent } from '../../../shared/components/alert/alert.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { FinalpageComponent } from '../../../shared/components/finalpage/finalpage.component';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { TranslationPipe } from '../../../shared/pipes/translation.pipe';
import { TrustHtmlPipe } from '../../../shared/pipes/trust-html.pipe';
import { CalendarPickerComponent } from '../../common/calendar-picker/calendar-picker.component';
import { CwPaymentMethodsComponent } from '../../common/cw-payment-methods/cw-payment-methods.component';
import { PersonalDataFormComponent } from '../../common/personal-data-form/personal-data-form.component';
import { CwCardHeaderComponent } from '../../common/theme/cw-card-header/cw-card-header.component';
import { CwNewCardComponent } from '../../common/theme/cw-new-card/cw-new-card.component';
import { EventAgendaViewComponent } from './event-agenda-view/event-agenda-view.component';
import { EventCardComponent } from './event-card/event-card.component';
import { EventCartOverviewComponent } from './event-cart-overview/event-cart-overview.component';
import { EventNavigationBarComponent } from './event-navigation-bar/event-navigation-bar.component';
import { EventSlotComponent } from './event-slot/event-slot.component';
import { getDomRoot } from '../../../services/dom-root.util';
import {
  adyenErrorMessageKey,
  isAlreadyBookedWithPayment,
  isBookingOutcomeUnknown,
  isPaidBookingStillCompleting,
  isPaymentSessionSpent,
  REFUND_NOTICE_KEY,
  refundNoticeKeyFor,
} from '../../../services/booking-outcome.util';

const component = [AlertComponent, FinalpageComponent, LoaderComponent, ButtonComponent, EventNavigationBarComponent, EventCardComponent, CalendarPickerComponent, EventSlotComponent, CwNewCardComponent, CwCardHeaderComponent, PersonalDataFormComponent, CwPaymentMethodsComponent, EventCartOverviewComponent, EventAgendaViewComponent];
const module = [FormsModule, TranslateModule];
const pipe = [DatePipe, TranslationPipe, TrustHtmlPipe];

@Component({
  selector: 'app-event',
  templateUrl: './event.component.html',
  styleUrls: ['./event.component.scss'],
  imports: [...component, ...module, ...pipe]
})
export class EventComponent implements OnInit {

  @Input() shownMsg = [];
  @Input() hiddenMsg = [];
  @Input() partner: Partner;
  @Input() lang: string;
  @Input() globals: GlobalObjects;
  @Input() widgetBookingInfo: WidgetBookingInfoDao;
  @Input() widgetColorConf: WidgetColorConf;
  @Input() widgetConf: any;
  @Input() selectedJsonCustomFields: {
    [key: number]: { type: string; value: any } | any;
  };
  @Input() utmSource: string;
  @Input() utmMedium: string;
  @Input() utmCampaign: string;
  @Input() utmContent: string;
  @Input() utmTerm: string;
  @Input() utmId: string;
  @Input() meeting_type_id: string;
  @Input() customer_notification_preference: string;
  @Input() uiMode: string;
  @Input() hideGridSwitcher = 'false';
  @Input() langSwitcher = true;
  @Input() selectedJsonCustomerDetail: CustomerPrefillDataModel;
  @Input() set filterStores(value: string[]) {
    // Check if the value is an array & feature is enabled; if not, assign an empty array
    this.eventFilterStores = (Array.isArray(value) && Number(this.partner?.is_group_appointment_store_feature_enabled)) ? value : [];
  }
  @Input() set filterCategories(value: string[]) {
    // Check if the value is an array & feature is enabled; if not, assign an empty array
    this.eventFilterCategories = (Array.isArray(value) && Number(this.partner?.is_group_appointment_category_feature_enabled)) ? value : [];
  }
  @Input() customer: CustomerDbModel;
  @Input() widgetTemplates: CustomBookingMessageTemplate[] = [];
  @Input() preApplyCoupon: string;
  @Input() timezones = new Array<TimezoneDbModel>();
  @Input() redirectedPaymentIntent = '';
  @Input() redirectedPaymentIntentClientSecret = '';
  @Input() eventStripeRedirectionData: any;
  /** An Adyen payment this tab parked before leaving the page, now back (see AdyenResumeService). */
  @Input() adyenResume?: AdyenResumeReturn;

  private captchaService = inject(CaptchaService);
  private customEventService = inject(CustomEventService);
  private customFieldService = inject(CustomFieldService);
  private destroyRef = inject(DestroyRef);
  private elementRef = inject(ElementRef);
  private eventCartService = inject(EventCartService);
  private eventsHelperService = inject(EventsHelperService);
  private formsService = inject(FormsService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private helperService = inject(HelperService);
  private localStorageService = inject(LocalStorageService);
  private partnerService = inject(PartnerService);
  private paymentService = inject(PaymentService);
  /** Book waits while the payment form is down behind a "check your email first" notice. */
  protected readonly payAgainRequired = this.paymentService.payAgainRequired;
  private timezoneService = inject(TimezoneService);
  private translateService = inject(TranslateService);
  private adyenResumeService = inject(AdyenResumeService);
  /** What cw-adyen parks before an Adyen payment leaves the page: the Stripe redirect snapshot. */
  protected readonly adyenResumeSnapshot = (): AdyenResumeSnapshot => ({
    host: 'event',
    data: this.buildRedirectionSnapshot(),
  });
  private utilService = inject(UtilService);
  private widgetUtilService = inject(WidgetUtilService);

  protected isWhitelabel(): boolean {
    return this.partnerService.hasFeature('is_whitelabel_enabled');
  }

  // When partner has opted into 'partner' mode, slot-fetch & booking payloads carry the partner's
  // configured timezone regardless of visitor's browser TZ. Falls back to visitor TZ otherwise.
  private get effectiveDisplayTimezone(): string {
    if (this.partner?.widget_display_timezone_source === WIDGET_DISPLAY_TIMEZONE_SOURCE.PARTNER && this.partner?.timezone) {
      return this.partner.timezone;
    }
    return this.userState?.timezone ?? this.timezoneService.localTimezone;
  }

  readonly widgetConstant = WIDGET_CONSTANTS;
  readonly templateContent = TRANSLATION_TEMPLATES;
  readonly bookingCustomFieldTypes = BOOKING_CUSTOM_FIELD_TYPES;
  readonly paymentTypes = PAYMENT_TYPES;
  protected readonly bookingRedirectionViews = BOOKING_REDIRECTION_VIEWS;

  protected maxAgendaViewDate: string;
  protected currentDate: string
  protected eventFilterStores: string[] = [];
  protected eventFilterCategories: string[] = [];
  private hasSecretEvents = false;

  paginationData: EventPaginationDao = {
    limit: EVENT_PAGINATION_LIMIT,
    offset: 0,
    totalCounts: 0,
    page: 0,
    hideLoadMoreButton: true,
    isLoading: false
  };
  viewDate: Date;
  old_select_calendar: CalendarMonthViewDay = null;
  disablePrev = true;
  days: CalendarMonthViewDay[] = [];
  calendarLoaded = false;
  terminLoaded = 1;
  noEvents = false;
  bookingStarted = false;
  booked = false; // Call final page with message if true
  isCustomFieldsLoaded = false;
  customFieldValues: {
    [key: number]:
    | {
      type: string;
      value: any;
      option_values: {
        custom_field_option_id: number;
        custom_field_option_value: string;
      }[];
    }
    | any;
  } = {};
  appointmentSlotGhostElements = [1, 2, 3, 4, 5, 6];
  tempArray: CustomFieldsDao[] = [];
  finalPage: boolean;
  cart: EventCartItem[] = [];
  bookedItems: EventCartItem[] = [];
  customerSelected: object;
  STATE_LS_NAME;
  CART_LS_NAME;
  USER_LS_NAME;
  FIRST_FREE_DATE;
  CUSTOMFIELDS_LS_NAME;
  BOOKED_ITEMS_LS_NAME;
  eventConstants = EVENT_CONSTANT;
  events: EventDao[];
  eventOnDate: EventDateDao[] = [];
  eventOnYear: EventDateDao[] = [];
  calendarEvents: CalendarMonthViewDay[] = [];
  environment: any;
  eventCustomFields = Array<CustomFieldsDao>();
  addonsCustomFields = Array<CustomFieldsDao>();
  baseUrl = environment.baseUrl;
  partnerName;
  fullPartnerName;
  formReseted = false;
  viewMode = EVENT_CONSTANT.EVENTS;
  noFreeDates: boolean;
  noFreeSlots: boolean;
  firstFreeDate;
  excludeWeekDays = [0, 1, 2, 3, 4, 5, 6];
  showAds = false;
  successMessage: string;
  courtesyForm: string;
  leadTime: number;
  totalTaxPrice: number;
  isWaitingListBookingMessageEnabled = false;
  showBookingLimitReachedMessageOnFinalPage = false;
  eventState: EventState = {
    eventId: null,
    date: null,
    additional_guests_enabled: 0,
    number_allowed_additional_guests: 0,
    is_multi_day: 0,
    has_one_slot: 0,
    slots: [],
    day: null,
    foundedSlot: null,
  };
  userState: UserState = {
    forename: null,
    lastName: null,
    gender: null,
    eMail: null,
    phone: null,
    mobile: null,
    birthday: null,
    customerId: null,
    customerUuid: null,
    street: null,
    zip: null,
    city: null,
    meeting_type_id: null,
    customer_notification_preference: null,
    timezone: null
  };
  additionalGuests: GuestModel[] = [];
  anonymousGuestCount = 0;
  availableEventDates: string[] = [];
  availableEventDatesCount: number;
  stripePaymentMethodId: string;
  showRefundNotice = false;
  refundNoticeKey = REFUND_NOTICE_KEY;
  refundTransactionId: string;
  storageComplete = false;
  totalCartDuration = 0;
  totalCartPrice = 0;
  customFieldsAddonsPrice = 0;
  noActiveEvents = false;
  noFilterEvents = false;
  screenType = 'GRID';
  paymentType: string;
  coupon: CouponDbModel;
  finalCouponDiscount = 0;
  couponDisableConfirmButton = false;
  isInvalidCoupon = false;
  bookingErrorMessage: string | string[];
  priceAfterCouponReduction: number;
  /** VAT on top of the discounted, participant-multiplied net (`plus`/`exclude` partners only). */
  cartVat = signal<VatResult>({ byRate: [], totalCents: 0 });
  paypalSetting: PayPalSettingsDbModel;
  isStripeEnabled = false;
  isOnlinePaymentMandatory = false;
  isEventOnlinePaymentEnabled = 0;
  isBexioEnabled = false;
  isStoreEnabled = false;
  isLexOfficePaymentTypeEnabled = false;
  isPaypalEnabled = false;
  isSaferpayEnabled = false;
  isAdyenEnabled = false;
  adyenPaymentDetails: AdyenPaymentDetails;
  /**
   * An Adyen payment is running: the hosted window is open, or Drop-in is authorising. Back, the
   * step bar, the cart, the personal data and Book stay locked until it reports back; the payment's
   * own "Cancel payment" is the way out, and it releases this through onAdyenReset().
   */
  protected readonly adyenPaymentInFlight = signal(false);
  nextButtonTemplate: CustomBookingMessageTemplate;
  previousButtonTemplate: CustomBookingMessageTemplate;
  bookButtonTemplate: CustomBookingMessageTemplate;
  dateLabelTemplate: CustomBookingMessageTemplate;
  showBookableDateNotAvailableIssue = false;
  automaticBookableDateProcessIsEnabled = false;
  automationBookingDatesLimitReached = false;
  hideSlotStep = false;
  calendarSlotsCountList: { date: Date; slotsCount: number }[] = [];
  showEventsListing = true;
  paypalOrderDetail: PayPalOrderDetailModel;
  saferPayPaymentDetails: SaferPayPaymentDetailsDbModel;
  isPaymentInProgress = false;
  hideBookingButton = false;
  disableBookingButton = false;
  stripeElementLoading = false;
  successFullBookings: {
    landingPageLink: string;
    bookingUUID: string;
  }[] = [];
  manageAppointmentNowTemplate: CustomBookingMessageTemplate;
  successTitleTemplate: CustomBookingMessageTemplate;
  downloadBookingDetailTemplate: CustomBookingMessageTemplate;
  widgetSuccessPageEventBookingLimitReachedTemplate: CustomBookingMessageTemplate;
  showFullscreenOverlay = false;
  calendarDate = false;
  agendaViewSelectedDate = this.utilService.dateToStr(new Date());
  protected bookButtonLoader = false;
  protected stripeConnectedAccountId: string;
  protected defaultPaymentType = '';
  protected showStripeRedirectionLoader = false;

  @ViewChild('eventsSection') eventsSectionRef: ElementRef;
  @ViewChild('eventForm') eventForm: NgForm;

  constructor() {
    this.translateService.onLangChange.subscribe(language => {
      this.lang = language.lang;
      this.createSuccessMessage(language.lang);
    });
  }

  ngOnInit() {
    // Set local storage fields
    this.setLocalStorageVariables();
    if (this.redirectedPaymentIntent && this.redirectedPaymentIntentClientSecret && this.eventStripeRedirectionData) {
      // Handle redirect from Stripe payment verification
      LoggerService.log('[Local Storage] Redirected from Stripe payment verification (Event booking)');

      // Set payment type to CREDITCARD
      this.paymentType = this.paymentTypes.CREDITCARD;

      // Set stripe payment intent ID from redirected payment intent
      this.stripePaymentMethodId = this.redirectedPaymentIntent;

      // Set loading states
      this.disableBookingButton = true;
      this.bookButtonLoader = true;
      this.bookingErrorMessage = undefined;
      this.showStripeRedirectionLoader = true;

      if (this.eventStripeRedirectionData) {
        // Restore local state for a consistent UX (optional)
        this.restoreRedirectionSnapshot(this.eventStripeRedirectionData);

        // Update stripe_payment_intent_id with the redirected payment intent ID
        this.eventStripeRedirectionData.bookingItems?.forEach((item: EventBookItem) => {
          if (item.payment_type === this.paymentTypes.CREDITCARD) {
            item.stripe_payment_intent_id = this.redirectedPaymentIntent;
          }
        });

        // Proceed with booking directly using stored bookingItems
        this.processBooking(this.eventStripeRedirectionData.bookingItems || []);
      } else {
        LoggerService.error('[Local Storage] Redirected payment intent exists but stored bookingItems are missing');
        this.bookingErrorMessage = 'Something is wrong with session store data. Please try again.';
        this.disableBookingButton = false;
        this.bookButtonLoader = false;
        this.showStripeRedirectionLoader = false;
      }
    } else if (this.adyenResume) {
      this.resumeAdyenPayment(this.adyenResume);
    }

    // set showEventsListing flag to false to prevent event listing flickering
    // when widget is configured for specific event
    if (this.globals?.eventId !== null && this.events?.length) {
      this.showEventsListing = false;
    }

    const timezone = this.userState.timezone || this.timezoneService.getLocalTimezone();

    this.viewDate = new Date(moment.tz(timezone).format(DATE_TIME_ISO_T_FORMAT));
    this.viewDate.setHours(0, 0, 0, 0);

    this.currentDate = moment.tz(timezone).format(DATE_ISO_FORMAT);
    this.maxAgendaViewDate = moment.tz(timezone).add(5, 'years').format(DATE_ISO_FORMAT);
    this.agendaViewSelectedDate = moment.tz(timezone).format(DATE_ISO_FORMAT);

    this.availableEventDates = [];

    this.noFreeDates = true;
    this.hiddenMsg[0] = false;
    this.partnerName = this.partner.bookingName;
    this.fullPartnerName = this.partner.name;
    this.environment = environment;

    this.widgetUtilService.getWidgetConf().subscriptionTypeId === 1 && (this.showAds = true);
    this.courtesyForm = this.widgetUtilService.getWidgetConf().courtesyForm;
    this.leadTime = this.widgetUtilService.getWidgetConf().eventLeadTime;

    // Setup initial templates
    this.setupInitialTemplates();
    this.createSuccessMessage(this.lang);

    this.customEventService.hideBookingButtonEvent.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((buttonState: { hide: boolean }) => this.hideBookingButton = buttonState.hide);

    this.customEventService.childCustomFieldsLoadedEvent.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(customFieldsLoaded =>
        this.tempArray = this.tempArray.concat(customFieldsLoaded)
      );
    this.customEventService.removeChildCustomFieldsEvent.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(ids => {
        this.tempArray = this.tempArray.filter(customField => ids.indexOf(customField.id) === -1)
      });

    if (this.uiMode) {
      if (this.uiMode === 'list') {
        this.screenType = 'LIST';
      } else if (this.uiMode === 'grid') {
        this.screenType = 'GRID';
      } else {
        this.screenType = 'LIST';
      }
    }

    this.customFieldService.addonsCustomFieldsPriceEvent.subscribe(() => {
      this.localStorageService.getItem(this.CUSTOMFIELDS_LS_NAME, (savedCustomFieldsState) => {
        if (savedCustomFieldsState) {
          this.customFieldsAddonsPrice = Object.values(savedCustomFieldsState as Record<string, { price?: number | number[] }>)
            .reduce((sum, item) =>
              sum + (item?.price
                ? Array.isArray(item.price)
                  ? item.price.reduce((a, b) => a + b, 0)
                  : item.price
                : 0),
              0);
          this.eventCartService.addonsCustomFieldsPrice = Number(this.customFieldsAddonsPrice);
          this.eventCartService.recalculateCart(this.cart);
          this.totalCartPrice = this.eventCartService.totalCartPrice;
          this.calculateCoupon();
          this.customEventService.addonsPriceChangeEvent.emit(this.customFieldsAddonsPrice);
        }
      });
    });
  }

  resetLandingPageLink(): void {
    this.bookingErrorMessage = undefined;
    this.successFullBookings = [];
    this.calendarSlotsCountList = [];
  }

  createSuccessMessage(lang: string): void {
    const eventBookingInfoTemplate = this.widgetTemplates.find(template =>
      template.identifier === WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_BOOKING_INFO_EVENT
    );
    eventBookingInfoTemplate && (eventBookingInfoTemplate.is_multi_language = 1);
    this.successMessage = this.utilService.getBookingInfoMessage(eventBookingInfoTemplate, lang);

    if (!this.successMessage) {
      const courtesyFormConstant = this.courtesyForm === 'Du' ? 'events.finalpage.successMessageDu' : 'events.finalpage.successMessageSie';
      this.translateService.get(courtesyFormConstant, { leadTime: this.leadTime })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(res => this.successMessage = res);
    }
    this.customEventService.finalSuccessMessageChangedEvent.emit(this.successMessage);
  }

  setLocalStorageVariables(): void {
    this.STATE_LS_NAME = EVENT_CONSTANT.EVENT_STATE_LS + this.partner.bookingName;
    this.CART_LS_NAME = EVENT_CONSTANT.EVENT_CART_LS + this.partner.bookingName;
    this.USER_LS_NAME = EVENT_CONSTANT.EVENT_GLOBAL_USER_LS + this.partner.bookingName;
    this.FIRST_FREE_DATE = EVENT_CONSTANT.FIRST_FREE_DATE + this.partner.bookingName;
    this.CUSTOMFIELDS_LS_NAME = EVENT_CONSTANT.EVENT_CUSTOM_FIELDS_LS + this.partner.bookingName;
    this.BOOKED_ITEMS_LS_NAME = EVENT_CONSTANT.EVENT_BOOKING_ITEMS_LS + this.partner.bookingName;
  }

  loadEvents(): void {
    this.noActiveEvents = this.noFilterEvents = this.noEvents = false;
    this.formsService.getEvents(this.paginationData.limit, this.paginationData.offset, this.eventFilterStores, this.eventFilterCategories)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: eventsData => {
          this.paginationData.totalCounts = eventsData?.total_count || 0;
          this.hasSecretEvents = eventsData?.has_secret || false;
          this.paginationData.hideLoadMoreButton = this.paginationData.totalCounts <= (Number(this.paginationData.limit) * (Number(this.paginationData.page) + 1))

          const timezone = this.userState.timezone || this.timezoneService.getLocalTimezone();
          for (const event of eventsData.events) {
            for (const item of event.slots) {
              if (item != null) {
                // Converting Utc date to local timezone date
                const startDate = item.start_utc || item.start;
                const endDate = item.end_utc || item.end;
                item.start = new Date(moment(startDate).tz(timezone).format(DATE_TIME_ISO_T_FORMAT));
                item.end = new Date(moment(endDate).tz(timezone).format(DATE_TIME_ISO_T_FORMAT));
                this.eventOnDate.push(item);

                item?.child_slots?.forEach(child => {
                  const startDate = child.start_utc || child.start;
                  const endDate = child.end_utc || child.end;
                  child.start = new Date(moment(startDate).tz(timezone).format(DATE_TIME_ISO_T_FORMAT));
                  child.end = new Date(moment(endDate).tz(timezone).format(DATE_TIME_ISO_T_FORMAT));
                });
              }
            }
          }

          if (this.paginationData.page === 0) {
            this.events = eventsData?.events || [];

            if (!this.globals?.isInternal) {
              this.events = this.events.filter(event => event.is_internal !== 1);
            }

            if (this.globals?.eventId) {
              const globalEventFound = this.events?.find(event => Number(event.id) === Number(this.globals.eventId) || event.uuid === this.globals.eventId);
              if (!globalEventFound) {
                this.formsService.getEventByIdOrUuid(this.globals.eventId)
                  .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    finalize(() => this.handleWidgetStates())
                  )
                  .subscribe({
                    next: eventDetails => eventDetails?.status && (this.globals?.isInternal || eventDetails.is_internal !== 1) && this.events.push(eventDetails)
                  });
              } else {
                this.handleWidgetStates();
              }
            } else {
              this.handleWidgetStates();
            }
          } else {
            if (eventsData?.events) {
              // If global event is present then filter it out from response as we have already loaded it
              let newlyLoadedEvents: EventDao[] = [];
              if (this.globals?.eventId) {
                newlyLoadedEvents = eventsData.events?.filter(event => Number(event.id) === Number(this.globals.eventId) || event.uuid === this.globals.eventId);
              } else {
                newlyLoadedEvents = eventsData.events;
              }

              if (!this.globals?.isInternal) {
                newlyLoadedEvents = newlyLoadedEvents.filter(event => event.is_internal !== 1);
              }

              this.events = [...this.events, ...newlyLoadedEvents] as EventDao[];
            }
          }
        },
        error: (error: HttpErrorResponse) => this.handleWidgetStates()
      });
  }

  eventFilterChangeEvents(params: [[], []]): void {
    this.eventFilterStores = params[0];
    this.eventFilterCategories = params[1];
    this.paginationData = {
      limit: EVENT_PAGINATION_LIMIT,
      offset: 0,
      totalCounts: 0,
      page: 0,
      hideLoadMoreButton: true,
      isLoading: false
    };
    this.loadEvents();
  }

  loadMoreEvents(paginationData: EventPaginationDao): void {
    this.paginationData = paginationData;
    this.loadEvents();
  }

  handleWidgetStates(): void {
    if (this.events.length) {
      !this.uiMode && (this.screenType = 'LIST');
      this.readLocalStorage();
    } else {
      if (this.eventFilterCategories.length || this.eventFilterStores.length) {
        this.hasSecretEvents ? (this.noActiveEvents = true) : (this.noFilterEvents = true);
      } else if (this.hasSecretEvents) {
        this.noActiveEvents = true;
      } else {
        this.noEvents = true;
      }
    }
  }

  autoLoadBookableDates() {
    const timezone = this.userState.timezone || this.timezoneService.getLocalTimezone();
    const nowInCurrentTz = moment.tz(timezone);

    const maxDate = new Date(nowInCurrentTz.add(this.partner.future_booking_threshold_events, 'days').format(DATE_TIME_ISO_T_FORMAT));
    const minDate = new Date(nowInCurrentTz.startOf('month').format(DATE_TIME_ISO_T_FORMAT));

    this.automaticBookableDateProcessIsEnabled = true;
    const isNextPressed = this.customEventService.eventCalendarIsNextPressed;

    const currMonthDate = new Date(
      this.viewDate.getFullYear(),
      ([true, undefined].includes(isNextPressed) ? this.viewDate.getMonth() + 1 : this.viewDate.getMonth() - 1),
      1
    );

    if (currMonthDate >= minDate && currMonthDate < maxDate) {
      this.viewDate = new Date(
        this.viewDate.getFullYear(),
        ([true, undefined].includes(isNextPressed) ? this.viewDate.getMonth() + 1 : this.viewDate.getMonth() - 1),
        1
      );

      const now = new Date(moment.tz(timezone).format(DATE_TIME_ISO_T_FORMAT));
      this.disablePrev = this.viewDate.getMonth() <= now.getMonth() && this.viewDate.getFullYear() <= now.getFullYear();
      this.eventOnDate = [];
      this.calendarLoaded = false;
    } else {
      this.calendarLoaded = true;
      this.automationBookingDatesLimitReached = true;
      this.calendarDate = true;

      // reset flags of next and previous calendar button
      LoggerService.log('[Reset flags of next and previous calendar button]');
      this.customEventService.eventCalendarIsNextPressed = undefined;
      this.customEventService.eventCalendarIsPreviousPressed = undefined;
    }
  }

  // Before calendar render
  markScheduleForEvent({ body }: { body: CalendarMonthViewDay[] }): void {
    const timezone = this.userState.timezone || this.timezoneService.getLocalTimezone();
    const dayOfMonth = this.utilService.dateToStr(
      new Date(body[10].date.getFullYear(), body[10].date.getMonth(), 1)
    );
    const maxDate = new Date(moment.tz(timezone).add(this.partner.future_booking_threshold_events, 'days').format(DATE_TIME_ISO_T_FORMAT));

    this.calendarEvents.length = 0;

    body.forEach((day) => {
      this.days.push(day);
      day.cssClass = 'cal-disabled';
      day.isWeekend = true;
    });

    this.formsService.getEventSlotsByEventIdAndDate(
      this.eventState.eventId,
      dayOfMonth,
      timezone
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: eventSlots => {
          const dateById = eventSlots.slotDates as string[];
          let slotIsSelected = false;
          if (!dateById || dateById.length === 0) {
            this.calendarLoaded = true;
            this.showBookableDateNotAvailableIssue = true;
            LoggerService.log('[disable_automatic_search]', this.partner.disable_automatic_search);
            if (Number(this.partner.disable_automatic_search) !== 1) {
              this.automaticBookableDateProcessIsEnabled = true;
            }
            if (this.automaticBookableDateProcessIsEnabled) {
              LoggerService.log('[autoLoadBookableDates]', 'auto checking for events');
              this.autoLoadBookableDates();
            }
            return;
          } else {
            this.automaticBookableDateProcessIsEnabled = false;
            this.showBookableDateNotAvailableIssue = false;

            // reset flags of next and previous calendar button
            LoggerService.log('[Reset flags of next and previous calendar button]');
            this.customEventService.eventCalendarIsNextPressed = undefined;
            this.customEventService.eventCalendarIsPreviousPressed = undefined;
          }
          this.availableEventDates = dateById;
          this.availableEventDatesCount = eventSlots.slotsCount;

          let selectedDate: CalendarMonthViewDay<any>;
          this.calendarLoaded = false;

          body.forEach((date) => {
            const fDate = this.utilService.dateToStr(date.date);
            for (let i = 0; i < Object.keys(dateById).length; i++) {
              if (dateById[i] === fDate) {
                date.isWeekend = false;
                date.cssClass = date.cssClass.replace('cal-disabled', '');
                this.calendarEvents.push(date);
                if (dateById.length === 1) {
                  selectedDate = date;
                }
              }

              if (i === Object.keys(dateById).length - 1) {
                this.calendarLoaded = true;
                this.calendarDate = true;
              }
            }

            if (
              this.eventState.date != null &&
              date.date.toDateString() ===
              this.eventState.date.toDateString() &&
              !slotIsSelected
            ) {
              slotIsSelected = true;
              date.cssClass = 'cal-selected';
              this.eventClicked({ day: date });
            }

            if (selectedDate && !slotIsSelected) {
              slotIsSelected = true;
              selectedDate.cssClass = 'cal-selected';
              this.eventClicked({ day: selectedDate });
            }
            if (date.date > maxDate) {
              date.cssClass = 'cal-disabled';
              date.isWeekend = true;
            }
          });

          if (!slotIsSelected && dateById?.length > 0) {
            body.forEach((date) => {
              const fDate = this.utilService.dateToStr(date.date);
              if (dateById[0] === fDate && new Date(dateById[0]) <= maxDate) {
                date.isWeekend = false;
                this.calendarEvents.push(date);
                selectedDate = date;
                slotIsSelected = true;
                date.cssClass = 'cal-selected';
                this.eventClicked({ day: date });
              }
            });
          }

          this.calendarLoaded = true;
        }
      });
  }

  // On next prev month
  viewDateChanged(event: { viewDate: Date; isNext: boolean }): void {
    const timezone = this.userState.timezone || this.timezoneService.getLocalTimezone();
    this.customEventService.eventCalendarIsNextPressed = event.isNext;
    this.customEventService.eventCalendarIsPreviousPressed = !event.isNext;
    this.automaticBookableDateProcessIsEnabled = false;
    this.showBookableDateNotAvailableIssue = false;
    this.automationBookingDatesLimitReached = false;
    this.terminLoaded = 0;
    this.calendarLoaded = false;
    const now = new Date(moment.tz(timezone).format(DATE_TIME_ISO_T_FORMAT));
    this.disablePrev = event.viewDate.getMonth() <= now.getMonth() && event.viewDate.getFullYear() <= now.getFullYear();
    this.eventOnDate = [];

    this.viewDate = event.viewDate;
    this.eventState.date = null;
  }

  eventClicked({ day }: { day: CalendarMonthViewDay }) {
    this.terminLoaded = 0;

    if (day.isWeekend || day.date === this.eventState.date) {
      this.terminLoaded = 1;
      return false;
    } else {
      const fDate = this.utilService.dateToStr(day.date);
      this.eventOnDate = [];
      this.eventState.date = day.date;
      this.eventState.day = day;

      day.cssClass = 'cal-selected';
      day.badgeTotal = 0;

      if (this.old_select_calendar != null) {
        this.old_select_calendar.cssClass = '';
      }

      this.old_select_calendar = day;
      this.formsService.getSlots(
        this.eventState.eventId,
        fDate,
        'day',
        this.partner.is_timezone_enabled ? this.effectiveDisplayTimezone : undefined
      )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: eventOnDate => {
            const timezone = this.userState.timezone || this.timezoneService.getLocalTimezone();
            if (!eventOnDate) {
              eventOnDate = [];
            }
            for (const item of eventOnDate) {
              if (item != null) {
                // Converting Utc date to local timezone date
                const startDate = item.start_utc || item.start;
                const endDate = item.end_utc || item.end;
                item.start = new Date(moment(startDate).tz(timezone).format(DATE_TIME_ISO_T_FORMAT));
                item.end = new Date(moment(endDate).tz(timezone).format(DATE_TIME_ISO_T_FORMAT));
                this.eventOnDate.push(item);

                item?.child_slots?.forEach(child => {
                  const startDate = child.start_utc || child.start;
                  const endDate = child.end_utc || child.end;
                  child.start = new Date(moment(startDate).tz(timezone).format(DATE_TIME_ISO_T_FORMAT));
                  child.end = new Date(moment(endDate).tz(timezone).format(DATE_TIME_ISO_T_FORMAT));
                });
              }
            }
            if (!this.cart.length) {
              this.bookIfOnlyOneEventIsAvailable(eventOnDate);
            }
            this.terminLoaded = 1;
          }
        });
    }
  }

  bookIfOnlyOneEventIsAvailable(eventOnDate: EventDateDao[]): void {
    if (eventOnDate?.length === 1) {
      const isSlotAvailable = this.eventsHelperService.isSlotAvailable(eventOnDate[0], this.bookedItems);

      if (isSlotAvailable) {
        if (
          this.partner.is_widget_event_single_slot_preselect_enabled === 1 &&
          (this.availableEventDatesCount === 1 || this.availableEventDates.length === 1)
        ) {
          this.totalCartDuration = Number(0);
          this.totalCartPrice = Number(0);
          this.cart = [];

          const cartSupported = this.widgetUtilService.getWidgetConf().cartSupported;
          if (!this.displayRegularData(eventOnDate[0]) && this.displayWaitingData(eventOnDate[0])) {
            eventOnDate[0].isWaitingList = true;
          }
          this.addToCart(eventOnDate[0], cartSupported);
        }
      } else {
        if (this.calendarEvents.length === 1) {
          LoggerService.log('[autoLoadBookableDates]', 'auto checking for next month events as previous month has single and not bookable slot');
          this.eventState.date = null;
          this.autoLoadBookableDates();
        }
      }
    }
  }

  onAutoSelectSlotAndJumpToSummaryEvent() {
    this.hideSlotStep = true;
    const cartSupported = this.widgetUtilService.getWidgetConf().cartSupported;
    const eventInfo = this.eventState.foundedSlot;
    this.eventCartService.addToCart(
      eventInfo,
      this.eventState,
      this.cart,
      this.CART_LS_NAME,
      this.events,
      cartSupported,
      (cart: EventCartItem[]) => {
        this.cart = cart;
        this.totalCartDuration = this.eventCartService.totalCartDuration;
        this.totalCartPrice = this.eventCartService.totalCartPrice;
        this.calculateCoupon();
        this.navigateTo(EVENT_CONSTANT.SUMMARY, true);
      }
    );
  }

  // Cart functions
  updateCart(eventData: any): void {
    if (eventData.event.target.checked) {
      const cartSupported = this.widgetUtilService.getWidgetConf().cartSupported;
      this.addToCart(eventData.eventInfo, (cartSupported ? true : false));
    } else {
      this.removeFromCart(eventData.eventInfo['id']);
    }
  }

  addToCart(eventInfo: EventDateDao, overwrite: boolean): void {
    const eventStateDate = this.eventState.date;

    this.eventCartService.addToCart(
      eventInfo,
      this.eventState,
      this.cart,
      this.CART_LS_NAME,
      this.events,
      overwrite,
      (cart: EventCartItem[]) => {
        this.cart = cart;
        this.totalCartDuration = this.eventCartService.totalCartDuration;
        this.totalCartPrice = this.eventCartService.totalCartPrice;

        if (!overwrite) {
          this.calendarSlotsCountList = [];
        }

        if (this.calendarSlotsCountList.length === 0) {
          this.calendarSlotsCountList = [{ date: eventStateDate, slotsCount: 1 }];
        } else {
          let calendarSlotsCount = this.calendarSlotsCountList.find(calendarSlot => eventStateDate?.toString() === calendarSlot?.date?.toString());
          if (calendarSlotsCount) {
            calendarSlotsCount.slotsCount++;
          } else {
            calendarSlotsCount = { date: eventStateDate, slotsCount: 1 };
            this.calendarSlotsCountList.push(calendarSlotsCount);
          }
        }

        // totalCartPrice just changed above — recompute unconditionally (previously only ran on
        // the calendarSlotsCountList "else" branch, so the FIRST item added to an empty cart left
        // cartVat/priceAfterCouponReduction stale).
        this.calculateCoupon();
      }
    );
  }

  removeFromCart(cartItemId: number): void {
    if (this.adyenPaymentInFlight()) {
      // The payment running now was minted for this cart.
      return;
    }
    this.eventCartService.removeFromCart(
      cartItemId,
      this.CART_LS_NAME,
      this.cart,
      (cart: EventCartItem[], deletedItem: EventCartItem) => {
        this.cart = cart;
        this.totalCartDuration = this.eventCartService.totalCartDuration;
        this.totalCartPrice = this.eventCartService.totalCartPrice;
        if (this.calendarSlotsCountList.length >= 0) {
          const calendarSlotsCount = this.calendarSlotsCountList.find(calendarSlot => deletedItem?.date?.toString() === calendarSlot.date.toString());
          if (calendarSlotsCount?.slotsCount > 1) {
            calendarSlotsCount.slotsCount--;
          } else {
            this.calendarSlotsCountList = this.calendarSlotsCountList.filter(calendarSlot => deletedItem.date.toString() !== calendarSlot.date.toString());
            this.eventState.day && (this.eventState.day.badgeTotal = 0);
          }
        }
        this.calculateCoupon();
        this.customEventService.cartItemsChangeEvent.emit({
          eventCart: this.cart,
          type: WIDGET_CONSTANTS.EVENT,
        });
      }
    );
  }

  // Change view and update local storage
  navigateTo(target: string, isFirstTime?: boolean): void {
    if (this.adyenPaymentInFlight()) {
      // Leaving the payment step would tear down the payment the shopper may be completing now.
      return;
    }
    const timezone = this.userState.timezone || this.timezoneService.getLocalTimezone();
    // to prevent event listing flickering when
    // widget is configured for specific event
    if (!isFirstTime) {
      this.showEventsListing = true;
    }

    if (
      !isFirstTime &&
      this.eventsSectionRef
    ) {
      this.eventsSectionRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
    }
    this.automaticBookableDateProcessIsEnabled = false;
    this.showBookableDateNotAvailableIssue = false;
    this.automationBookingDatesLimitReached = false;
    this.coupon = undefined;
    this.finalCouponDiscount = 0;

    if (target === EVENT_CONSTANT.EVENTS || target === EVENT_CONSTANT.DATE) {
      this.paypalOrderDetail = undefined;
      this.saferPayPaymentDetails = undefined;
    this.adyenPaymentDetails = undefined;
      this.stripePaymentMethodId = undefined;
      this.refundTransactionId = undefined;
      this.isPaymentInProgress = false;
      this.showRefundNotice = false;
    }

    this.calendarLoaded = false;
    if (!isFirstTime) {
      this.scrollToEventComponent();
    }

    // trigger start event on first user interaction
    this.customEventService.triggerGroupApoointmentStartEvent();

    if (target === 'events') {
      this.globals.eventId = null;
      Object.assign(this.eventState, {
        eventId: null,
        date: null,
        additional_guests_enabled: 0,
        number_allowed_additional_guests: 0,
        is_multi_day: 0,
        has_one_slot: 0,
        slots: [],
        day: null,
        foundedSlot: null,
      });
      this.utilService.updateLocalStorage(
        this.STATE_LS_NAME,
        this.eventState
      );
      this.viewMode = target;
      this.googleAnalyticsService.emitBookingEvent(
        GTM_EVENTS.EVENT_BOOKING_STEP_SELECT_EVENT,
        this.prepareAnalyticsPayload()
      );
    } else if (target === 'date') {
      if (this.eventState.is_agenda_view_enabled === 1) {
        this.viewMode = target;
        this.handelAgendaView();
      } else {
        this.terminLoaded = 0;
        this.eventOnDate = [];
        const dayOfMonth = moment.tz(timezone).format(DATE_ISO_FORMAT);
        this.formsService.getEventSlotsByEventIdAndDate(
          this.eventState.eventId,
          dayOfMonth,
          timezone
        )
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: eventSlots => {
              const dateById = eventSlots.slotDates as string[];
              this.availableEventDatesCount = eventSlots.slotsCount;
              if (Object?.keys(dateById)?.length > 0) {
                this.noFreeDates = false;
                this.firstFreeDate = new Date(dateById[0]);
                this.utilService.updateLocalStorage(
                  this.FIRST_FREE_DATE,
                  this.firstFreeDate
                );
                this.viewDate = this.firstFreeDate;
                this.viewDateChanged({ viewDate: this.viewDate, isNext: true });
              } else {
                this.noFreeDates = true;
                this.calendarLoaded = true;
              }
              this.viewMode = target;
              // Fetch custom fields
              this.fetchCustomFields();
              this.formReseted = false;
              this.utilService.updateLocalStorage(
                this.STATE_LS_NAME,
                this.eventState
              );
              this.googleAnalyticsService.emitBookingEvent(
                GTM_EVENTS.EVENT_BOOKING_STEP_AVAILABLE_EVENTS,
                this.prepareAnalyticsPayload()
              );
            },
            error: (err) => LoggerService.error(err.message)
          });
      }
    } else {
      if (target === this.eventConstants.SUMMARY) {
        this.resetDataBeforeLoadingSummaryPage();
        this.verifyOnlinePaymentStatus();
        this.googleAnalyticsService.emitBookingEvent(
          GTM_EVENTS.EVENT_BOOKING_STEP_SUMMARY,
          this.prepareAnalyticsPayload()
        );
      }
      this.viewMode = target;
      // Fetch custom fields
      this.fetchCustomFields();
      this.formReseted = false;
      this.utilService.updateLocalStorage(this.STATE_LS_NAME, this.eventState);
    }
  }

  fetchCustomFields(): void {
    this.isCustomFieldsLoaded = false;
    this.tempArray = [];
    this.eventCustomFields = [];
    if (!this.eventState.eventId) {
      return;
    }
    this.formsService
      .getEventCustomFields(this.eventState.eventId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: appointmentCustomFields => {
          if (!appointmentCustomFields) {
            appointmentCustomFields = [];
          }

          for (const appointmentCustomField of appointmentCustomFields) {
            this.tempArray.push(appointmentCustomField);
          }

          // Clean orphaned custom field values from cache
          // When admin removes a custom field from event, cached values may persist in localStorage
          // This cleanup ensures orphaned field values don't get submitted to backend
          const validFieldIds = new Set(appointmentCustomFields.map(f => f.id));
          if (this.customFieldValues && Object.keys(this.customFieldValues).length > 0) {
            const cachedFieldIds = Object.keys(this.customFieldValues);
            let hasOrphanedFields = false;

            for (const cachedId of cachedFieldIds) {
              if (!validFieldIds.has(Number(cachedId))) {
                delete this.customFieldValues[cachedId];
                hasOrphanedFields = true;
              }
            }

            // Update localStorage if orphaned fields were removed
            if (hasOrphanedFields) {
              this.utilService.updateLocalStorage(this.CUSTOMFIELDS_LS_NAME, this.customFieldValues);
            }
          }

          // Translate any identifier-keyed prefill entries to their numeric custom-field id
          // once, before the id-keyed prefill loop below reads selectedJsonCustomFields.
          this.selectedJsonCustomFields = this.helperService.resolveCustomFieldIdentifierKeys(this.selectedJsonCustomFields, this.tempArray);

          const trimmedArray: CustomFieldsDao[] = [];
          const values: number[] = [];
          // removing the duplicates
          for (
            let y = 0, len = Object.keys(this.tempArray).length;
            y < len;
            y++
          ) {
            const value: number = this.tempArray[y]['id'];
            if (
              values.indexOf(value) === -1 &&
              !this.tempArray[y]['parent_custom_field_id']
            ) {
              trimmedArray.push(this.tempArray[y]);
              if (
                this.tempArray[y].type === this.bookingCustomFieldTypes.SELECT ||
                this.tempArray[y].type === this.bookingCustomFieldTypes.CARD_SELECT ||
                this.tempArray[y].type === this.bookingCustomFieldTypes.IMAGE_SELECT
              ) {
                // Prevet duplication of default option
                if (
                  this.tempArray[y].custom_field_options.length &&
                  this.tempArray[y].custom_field_options[0]['id'] != null &&
                  this.tempArray[y].type !== this.bookingCustomFieldTypes.CARD_SELECT &&
                  this.tempArray[y].type !== this.bookingCustomFieldTypes.IMAGE_SELECT &&
                  this.tempArray[y].type !== this.bookingCustomFieldTypes.SELECT
                ) {
                  const tempCustomFieldOptions = new CustomFieldOptions();
                  tempCustomFieldOptions.id = null;
                  this.tempArray[y].custom_field_options.unshift(tempCustomFieldOptions);
                }
                if (!this.customFieldValues[this.tempArray[y].id]) {
                  if (this.tempArray[y].is_multiple_select === 1) {
                    const temp =
                      this.selectedJsonCustomFields &&
                        this.selectedJsonCustomFields[this.tempArray[y].id]
                        ? this.selectedJsonCustomFields[this.tempArray[y].id]
                        : undefined;
                    // temp is a comma-string on the querystring path, but the webcomponent's
                    // selected-custom-fields JSON can deliver an array/number for a multi-select —
                    // handle all three so .split() never throws "split is not a function".
                    const finalValue = temp
                      ? (Array.isArray(temp)
                        ? temp.map((item: any) => Number(item))
                        : String(temp).split(',').map((item: string) => Number(item)))
                      : [];
                    this.customFieldValues[this.tempArray[y].id] = {
                      value: finalValue,
                      type: this.tempArray[y].type,
                    };
                  } else {
                    if (this.tempArray[y].type === this.bookingCustomFieldTypes.SELECT) {
                      if (
                        this.selectedJsonCustomFields &&
                        this.selectedJsonCustomFields[this.tempArray[y].id]
                      ) {
                        this.customFieldValues[this.tempArray[y].id] = Number(
                          this.selectedJsonCustomFields[this.tempArray[y].id]
                        );
                      } else if (this.customFieldValues[this.tempArray[y].id]) {
                        // Do nothing
                      } else {
                        this.customFieldValues[this.tempArray[y].id] = {
                          value: '',
                          type: this.bookingCustomFieldTypes.SELECT,
                        };
                      }
                    } else if (
                      this.tempArray[y].type === this.bookingCustomFieldTypes.CARD_SELECT ||
                      this.tempArray[y].type === this.bookingCustomFieldTypes.IMAGE_SELECT
                    ) {
                      if (this.selectedJsonCustomFields && this.selectedJsonCustomFields[this.tempArray[y].id]) {
                        this.customFieldValues[this.tempArray[y].id] = {
                          value: [Number(this.selectedJsonCustomFields[this.tempArray[y].id])],
                          type: this.tempArray[y].type,
                        };
                      } else if (this.customFieldValues[this.tempArray[y].id]) {
                        // Do nothing
                      } else {
                        this.customFieldValues[this.tempArray[y].id] = {
                          value: [],
                          type: this.tempArray[y].type,
                        };
                      }
                    }
                  }
                }
              } else if (this.tempArray[y].type === this.bookingCustomFieldTypes.NUMBER_SELECT) {
                const optionIds: number[] = this.tempArray[
                  y
                ].custom_field_options.map(
                  (item: CustomFieldOptions) => item.id
                );
                const optionValues: {
                  custom_field_option_id: number;
                  custom_field_option_value: any;
                }[] = this.tempArray[y].custom_field_options.map(
                  (item: CustomFieldOptions) => {
                    item.option_value = item.number_default;
                    return {
                      custom_field_option_id: item.id,
                      custom_field_option_value: item.number_default,
                    };
                  }
                );

                this.customFieldValues[this.tempArray[y].id] = {
                  value: optionIds,
                  option_values: optionValues,
                  type: this.tempArray[y].type,
                };
              } else if (this.tempArray[y].type === this.bookingCustomFieldTypes.FILE) {
                this.customFieldValues[this.tempArray[y].id] = {
                  type: this.bookingCustomFieldTypes.FILE,
                  file_ids: this.customFieldValues[this.tempArray[y].id]?.file_ids?.length > 0 ? this.customFieldValues[this.tempArray[y].id]?.file_ids : [],
                };
              } else {
                if (
                  this.tempArray[y].type === this.bookingCustomFieldTypes.TEXT ||
                  this.tempArray[y].type === this.bookingCustomFieldTypes.TEXTAREA ||
                  this.tempArray[y].type === this.bookingCustomFieldTypes.RADIO ||
                  this.tempArray[y].type === this.bookingCustomFieldTypes.DATE
                ) {
                  if (this.selectedJsonCustomFields && this.selectedJsonCustomFields[this.tempArray[y].id]) {
                    this.customFieldValues[this.tempArray[y].id] = this.selectedJsonCustomFields[this.tempArray[y].id];
                  } else {
                    this.customFieldValues[this.tempArray[y].id] = this.customFieldValues?.[this.tempArray?.[y]?.id] || undefined;
                  }
                } else if (this.tempArray[y].type === this.bookingCustomFieldTypes.CHECKBOX) {
                  if (this.selectedJsonCustomFields && this.selectedJsonCustomFields[this.tempArray[y].id]) {
                    this.customFieldValues[this.tempArray[y].id] = ["true", "1", true, 1].includes(this.selectedJsonCustomFields[this.tempArray[y].id]) ? true : false;
                  } else {
                    this.customFieldValues[this.tempArray[y].id] = this.customFieldValues[this.tempArray[y].id] ? ["true", "1", true, 1].includes(this.customFieldValues[this.tempArray[y].id]) ? true : false : false;
                  }
                }
              }

              values.push(value);
            }
          }
          // sorting of custom fields by position
          trimmedArray.sort(function (a, b) {
            return a.position - b.position;
          });

          this.addonsCustomFields = trimmedArray.filter(customField => customField.is_addon);
          this.eventCustomFields = trimmedArray.filter(customField => !customField.is_addon);
          this.isCustomFieldsLoaded = true;
        },
        error: (err: HttpErrorResponse) => {
          LoggerService.error(err);
          this.isCustomFieldsLoaded = true;
        }
      });
  }

  resetForm(): void {
    if (this.partner.hide_back_on_bookingsite === 1) {
    } else {
      this.removeEventState();
    }
    this.removeUserState();
    this.removeCartState();
    this.removeCustomFieldsState();

    // Go back to first tab
    if (this.partner.hide_back_on_bookingsite === 1) {
      this.viewMode = EVENT_CONSTANT.DATE;
    } else {
      this.viewMode = EVENT_CONSTANT.EVENTS;
    }

    // Show success message
    this.formReseted = true;
  }

  removeEventState(): void {
    this.localStorageService.removeItem(this.STATE_LS_NAME, () => {
      // Clear form inputs — use Object.assign to preserve reference for child components
      Object.assign(this.eventState, {
        eventId: null,
        date: null,
        additional_guests_enabled: 0,
        number_allowed_additional_guests: 0,
        is_multi_day: 0,
        has_one_slot: 0,
        slots: [],
        day: 0,
        foundedSlot: null,
      });
    });
  }

  removeUserState(): void {
    this.localStorageService.removeItem(this.USER_LS_NAME, () => {
      // Clear form inputs
      this.userState = {
        forename: null,
        lastName: null,
        gender: null,
        eMail: null,
        phone: null,
        mobile: null,
        customerId: null,
        birthday: null,
        customerUuid: null,
        street: null,
        zip: null,
        city: null,
        meeting_type_id: null,
        timezone: this.userState.timezone
      };
    });
  }

  removeCartState(): void {
    this.localStorageService.removeItem(this.CART_LS_NAME, () => {
      // Clear form inputs
      this.cart = [];
    });
  }

  removeCustomFieldsState(): void {
    this.localStorageService.removeItem(this.CUSTOMFIELDS_LS_NAME, () => {
      // Clear form inputs
      this.customFieldValues = {};
    });
  }

  readLocalStorage(): void {
    // Read local storage to set form state
    this.readEventState();
    // Read local storage to set user state
    this.readUserState();
    // Read local storage to set cart state
    this.readCartState();
    // Read local storage to set custom fields state
    this.readCustomFieldsState();
    // Read local storage to set booked state
    this.readBookingState();
    // Read local storage to set first free appointment month
    this.readFreeStateState();
  }

  readEventState(): void {
    this.localStorageService.getItem(this.STATE_LS_NAME, (savedFormState: EventState) => {
      if (savedFormState) {
        this.eventState = savedFormState;

        if (this.globals?.eventId) {
          this.eventState.eventId = this.globals?.eventId;
          this.eventState.date = null;
          if (this.checkIfEventHasSlots(this.eventState.eventId)) {
            this.onAutoSelectSlotAndJumpToSummaryEvent();
            return;
          }
        }
        this.updateEventState();
        // if event selected go to another tab
        if (this.eventState.eventId) {
          const today = new Date();
          const dayOfMonth = this.utilService.dateToStr(
            new Date(today.getFullYear(), today.getMonth(), 1)
          );

          this.formsService.getEventSlotsByEventIdAndDate(
            this.eventState.eventId,
            dayOfMonth,
            this.userState?.timezone ?? this.timezoneService.localTimezone
          )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: eventSlots => {
                const dateById = eventSlots.slotDates as string[];
                this.availableEventDatesCount = eventSlots.slotsCount;
                if (Object.keys(dateById)?.length > 0) {
                  this.firstFreeDate = new Date(dateById[0]);
                  this.utilService.updateLocalStorage(
                    this.FIRST_FREE_DATE,
                    this.firstFreeDate
                  );
                  this.viewDate = this.firstFreeDate;
                  this.noFreeDates = false;
                  this.viewMode = EVENT_CONSTANT.DATE;
                  this.viewDateChanged({ viewDate: this.viewDate, isNext: true });
                } else {
                  this.noFreeDates = true;
                  this.removeEventState();
                }
              },
              error: (err) => LoggerService.error(err.message)
            });
        }
      } else {
        if (!this.eventState.eventId && this.globals?.eventId) {
          const event = this.events.find(event => event.uuid == this.globals?.eventId || event.id == Number(this.globals?.eventId));
          if (event?.status === 1) {
            this.eventState.eventId = this.globals?.eventId;
            this.eventState.date = null;
            if (this.checkIfEventHasSlots(this.eventState.eventId)) {
              this.onAutoSelectSlotAndJumpToSummaryEvent();
              return;
            }
            this.updateEventState();
            this.navigateTo(EVENT_CONSTANT.DATE, true);
          }
        }
      }
    });
  }

  checkIfEventHasSlots(eventId: string | number): boolean {
    if (!this.events || this.events?.length === 0) {
      return false;
    }
    const foundedEvent: EventDao = this.events.find((item: EventDao) => {
      if (eventId == item.uuid || eventId == item.id) {
        return true;
      } else {
        return false;
      }
    });
    if (foundedEvent) {
      if (
        foundedEvent.has_one_slot === 1 &&
        foundedEvent.slots &&
        foundedEvent.slots.length > 0
      ) {
        let foundedSlot: EventDateDao;
        for (const slot of foundedEvent.slots) {
          if (slot.start && !moment().isAfter(slot.start)) {
            foundedSlot = Object.assign({}, slot);
            break;
          }
        }
        if (!foundedSlot) {
          return false;
        }
        if (foundedSlot.regular_booking_count < foundedSlot.regular_capacity) {
          this.eventState.foundedSlot = foundedSlot;
          this.eventState.slots = foundedEvent.slots;
          this.eventState.is_multi_day = foundedEvent.is_multi_day;
          this.eventState.additional_guests_enabled = foundedEvent.additional_guests_enabled;
          this.eventState.number_allowed_additional_guests = foundedEvent.number_allowed_additional_guests;
          this.eventState.guest_entry_mode = foundedEvent.guest_entry_mode ?? 'named_only';
          this.eventState.has_one_slot = 1;
          this.eventState.is_widget_event_guest_email_required = foundedEvent.is_widget_event_guest_email_required;
          this.eventState.is_widget_event_guest_phone_required = foundedEvent.is_widget_event_guest_phone_required;
          this.eventState.is_agenda_view_enabled = foundedEvent.is_agenda_view_enabled;
          return true;
        } else {
          LoggerService.warn('[Debug] Automatic jump is disabled because regular booking count is greater or equal to regular capacity');
          return false;
        }
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  updateEventState(): void {
    for (const event of this.events) {
      if (event.uuid == this.eventState.eventId || event.id == this.eventState.eventId) {
        this.eventState.additional_guests_enabled = event.additional_guests_enabled;
        this.eventState.number_allowed_additional_guests = event.number_allowed_additional_guests;
        this.eventState.guest_entry_mode = event.guest_entry_mode ?? 'named_only';
        this.eventState.is_multi_day = event.is_multi_day;
        this.eventState.meeting_type_id = event.meeting_type_id;
        this.eventState.meeting_type = event.meeting_type;
        this.eventState.place = event.place;
        this.eventState.is_widget_event_guest_email_required = event.is_widget_event_guest_email_required;
        this.eventState.is_widget_event_guest_phone_required = event.is_widget_event_guest_phone_required;
        this.eventState.is_agenda_view_enabled = Number(event.is_agenda_view_enabled);
      }
    }
  }

  readUserState(): void {
    this.localStorageService.getItem(
      this.USER_LS_NAME,
      (savedUserState: UserState) => {
        if (savedUserState) {
          this.userState = savedUserState;
        }
      }
    );
  }

  readCartState(): void {
    this.localStorageService.getItem(
      this.CART_LS_NAME,
      (savedCartState: EventCartItem[]) => {
        if (savedCartState) {
          this.cart = savedCartState;

          this.eventCartService.recalculateCart(this.cart);
          this.totalCartDuration = this.eventCartService.totalCartDuration;
          this.totalCartPrice = this.eventCartService.totalCartPrice;
        }
      }
    );
  }

  readCustomFieldsState(): void {
    this.localStorageService.getItem(
      this.CUSTOMFIELDS_LS_NAME,
      (savedCustomFieldsState: any) => {
        if (savedCustomFieldsState) {
          this.customFieldValues = savedCustomFieldsState;
        }
      }
    );
  }

  readBookingState(): void {
    this.localStorageService.getItem(
      this.BOOKED_ITEMS_LS_NAME,
      (savedBookedItems: EventCartItem[]) => {
        if (savedBookedItems) {
          this.bookedItems = savedBookedItems;
        }
      }
    );
  }

  readFreeStateState(): void {
    this.localStorageService.getItem(
      this.FIRST_FREE_DATE,
      (savedFreeEventDate: Date) => {
        if (savedFreeEventDate) {
          this.viewDate = savedFreeEventDate;
        }
      }
    );
  }

  private book(): void {
    if (this.bookValidated() && this.isOnlinePaymentBookButtonValidated()) {
      this.doBooking();
    } else {
      LoggerService.error('Validation failed');
    }
  }

  // Booking action
  doBooking(): void {
    this.disableBookingButton = true;
    this.bookButtonLoader = true;

    this.bookingErrorMessage = undefined;

    const bookingItems = this.prepareBookingItems();
    this.processBooking(bookingItems);
  }

  private prepareBookingItems(): EventBookItem[] {
    // Extract custom field values
    let fieldIds = Object.keys(this.customFieldValues);
    const rawCustomFieldIds: number[] = this.tempArray.map((i) => i.id);

    if (rawCustomFieldIds?.length > 0) {
      fieldIds = fieldIds.filter((id: any) => {
        return rawCustomFieldIds.indexOf(Number(id)) > -1;
      });
    }

    const customFieldValues: {
      custom_field_id: number;
      type: string;
      value?: any;
      file_ids?: any;
      option_values?: {
        custom_field_option_id: number;
        custom_field_option_value: string;
      }[];
    }[] = [];

    for (let i = 0; i < fieldIds.length; i++) {
      if (this.customFieldValues[fieldIds[i]]?.type === this.bookingCustomFieldTypes.SELECT) {
        if (Array.isArray(this.customFieldValues[fieldIds[i]]?.value)) {
          customFieldValues.push({
            custom_field_id: Number(fieldIds[i]),
            type: this.bookingCustomFieldTypes.SELECT,
            value: this.customFieldValues[fieldIds[i]]?.value,
          });
        } else {
          customFieldValues.push({
            custom_field_id: Number(fieldIds[i]),
            type: this.bookingCustomFieldTypes.SELECT,
            value: [this.customFieldValues[fieldIds[i]]?.value],
          });
        }
      } else if (this.customFieldValues[fieldIds[i]]?.type === this.bookingCustomFieldTypes.CARD_SELECT) {
        customFieldValues.push({
          custom_field_id: Number(fieldIds[i]),
          type: this.bookingCustomFieldTypes.CARD_SELECT,
          value: this.customFieldValues[fieldIds[i]]?.value,
        });
      } else if (this.customFieldValues[fieldIds[i]]?.type === this.bookingCustomFieldTypes.IMAGE_SELECT) {
        customFieldValues.push({
          custom_field_id: Number(fieldIds[i]),
          type: this.bookingCustomFieldTypes.IMAGE_SELECT,
          value: this.customFieldValues[fieldIds[i]]?.value,
        });
      } else if (this.customFieldValues[fieldIds[i]]?.type === this.bookingCustomFieldTypes.NUMBER_SELECT) {
        customFieldValues.push({
          custom_field_id: Number(fieldIds[i]),
          type: this.bookingCustomFieldTypes.NUMBER_SELECT,
          value: this.customFieldValues[fieldIds[i]]?.value,
          option_values: this.customFieldValues[fieldIds[i]]?.option_values,
        });
      } else if (this.customFieldValues[fieldIds[i]]?.type === this.bookingCustomFieldTypes.FILE) {
        customFieldValues.push({
          custom_field_id: Number(fieldIds[i]),
          type: this.bookingCustomFieldTypes.FILE,
          file_ids: Object.assign([], this.customFieldValues[fieldIds[i]]?.file_ids),
        });
      } else {
        let tempCustomFieldsDao: CustomFieldsDao;
        if (this.tempArray?.length > 0) {
          tempCustomFieldsDao = this.tempArray.find((item) => {
            return Number(item.id) === Number(fieldIds[i]);
          });
        }

        customFieldValues.push({
          custom_field_id: Number(fieldIds[i]),
          type: tempCustomFieldsDao ? tempCustomFieldsDao?.type : '',
          value: this.customFieldValues[fieldIds[i]]?.toString(),
        });
      }
    }

    // Ensure hidden custom fields are included with proper empty values
    const hiddenSelectTypes = [
      this.bookingCustomFieldTypes.SELECT,
      this.bookingCustomFieldTypes.CARD_SELECT,
      this.bookingCustomFieldTypes.IMAGE_SELECT,
      this.bookingCustomFieldTypes.NUMBER_SELECT,
      this.bookingCustomFieldTypes.WORKER_SELECT,
    ];
    for (const hiddenField of this.tempArray.filter(cf => cf.is_hidden === 1)) {
      if (hiddenField.type === this.bookingCustomFieldTypes.FILE) {
        continue;
      }
      const existingIdx = customFieldValues.findIndex(cf => cf.custom_field_id === hiddenField.id);
      if (existingIdx > -1) {
        const entry = customFieldValues[existingIdx];
        if (entry.value === undefined || entry.value === null) {
          entry.value = hiddenSelectTypes.includes(hiddenField.type) ? [] : '';
        } else if (Array.isArray(entry.value) && entry.value.includes(null)) {
          entry.value = entry.value.filter((v: number | null) => v !== null);
        }
      } else {
        customFieldValues.push({
          custom_field_id: Number(hiddenField.id),
          type: hiddenField.type,
          value: hiddenSelectTypes.includes(hiddenField.type) ? [] : '',
        });
      }
    }

    // Create post body
    const bookingItems: EventBookItem[] = [];
    for (const item of this.cart) {
      this.isWaitingListBookingMessageEnabled = item.is_waitinglist;

      const bookingData: EventBookItem = {
        event_id: item.eventId,
        event_name: item.referenceEvent?.title,
        category: item.category,
        amount: item.currentEvent.price * (1 + (this.additionalGuests?.length || 0) + (this.anonymousGuestCount || 0)),
        is_waitinglist: item.is_waitinglist,
        slot_id: item['slotId'],
        title: this.userState.title,
        prename: this.userState.forename,
        lastname: this.userState.lastName,
        gender: this.userState.gender,
        email: this.userState.eMail,
        phone: this.userState.phone,
        mobile: this.userState.mobile,
        birthday: this.userState?.birthday ? moment(this.userState?.birthday).format(DATE_ISO_FORMAT) : undefined,
        country: this.userState.country_id,
        customer_notification_preference: this.userState.customer_notification_preference,
        customer_id: this.userState.customerId,
        customer_uuid: this.userState.customerUuid,
        company_name: (this.userState?.company || undefined),
        internal_comment: (this.userState?.internal_comment || undefined),
        comment: this.userState.comment,
        meeting_type_id: this.eventState.meeting_type_id || this.userState.meeting_type_id,
        customFieldValues: customFieldValues,
        user_browser_language: this.globals.user_browser_language,
        user_selected_language: this.globals.user_selected_language,
        guests: this.additionalGuests,
        anonymous_guest_count: this.anonymousGuestCount > 0 ? this.anonymousGuestCount : undefined,
        worker_id: item.workerId,
        childSlots: item.selectedSlot ? [item.selectedSlot] : [],
        coupon_code: this.coupon && !this.isInvalidCoupon ? this.coupon.code : null,
        payment_type: this.paymentType,
        appointment_source_id: this.globals?.isInternal ? APPOINTMENT_SOURCE_ID.INTERNAL_WIDGET : APPOINTMENT_SOURCE_ID.EXTERNAL_WIDGET,
        utm_source: ((this.utmSource && this.utmSource !== 'null') ? this.utmSource : undefined),
        utm_content: ((this.utmContent && this.utmContent !== 'null') ? this.utmContent : undefined),
        utm_medium: ((this.utmMedium && this.utmMedium !== 'null') ? this.utmMedium : undefined),
        utm_campaign: ((this.utmCampaign && this.utmCampaign !== 'null') ? this.utmCampaign : undefined),
        utm_term: ((this.utmTerm && this.utmTerm !== 'null') ? this.utmTerm : undefined),
        utm_id: ((this.utmId && this.utmId !== 'null') ? this.utmId : undefined),
        ...(this.userState.acceptDataPolicy !== undefined) && { accept_data_policy: +this.userState.acceptDataPolicy },
        ...(this.partner?.show_address_on_widget !== 0 ? {
          street: this.userState.street,
          zip: this.userState.zip,
          city: this.userState.city
        } : {}),
        friendly_captcha: (this.userState?.friendly_captcha_challenge || undefined),
        timezone: this.userState.timezone,
      };

      if (this.paymentType === this.paymentTypes.PAYPAL) {
        bookingData.paypal_order_id = this.paypalOrderDetail.paypal_order_id;
        bookingData.paypal_payer_id = this.paypalOrderDetail.paypal_payer_id;
        bookingData.paypal_status = this.paypalOrderDetail.paypal_status;
        bookingData.paypal_raw_data = this.paypalOrderDetail.paypal_raw_data;
        bookingData.paypal_capture_id = this.paypalOrderDetail.paypal_capture_id;
        bookingData.paypal_payment_log_id = this.paypalOrderDetail.paypal_payment_log_id;
      } else if (this.paymentType === this.paymentTypes.SAFERPAY) {
        bookingData.saferpay_transaction_id = this.saferPayPaymentDetails.transaction_id;
        bookingData.saferpay_payment_method = this.saferPayPaymentDetails.payment_method;
      } else if (this.paymentType === this.paymentTypes.ADYEN) {
        // Opaque handles only. The backend re-reads the session from Adyen
        // and believes that, never what the browser says was paid.
        bookingData.adyen_session_id = this.adyenPaymentDetails?.session_id;
        bookingData.adyen_session_result = this.adyenPaymentDetails?.session_result;
      } else if (this.paymentType === this.paymentTypes.CREDITCARD) {
        bookingData.stripe_payment_intent_id = this.stripePaymentMethodId ? this.stripePaymentMethodId : null;
      }

      bookingItems.push(bookingData);
    }

    for (let i = 0; i < fieldIds.length; i++) {
      if (this.customFieldValues[fieldIds[i]]?.type === this.bookingCustomFieldTypes.FILE) {
        this.customFieldValues[fieldIds[i]].file_ids = [];
      }
    }

    this.utilService.updateLocalStorage(this.CUSTOMFIELDS_LS_NAME, this.customFieldValues);
    this.userState.captcha_challenge = undefined;
    this.utilService.updateLocalStorage(this.USER_LS_NAME, this.userState);

    return bookingItems;
  }

  /**
   * First human-readable message of a failed /events/book or /appointments/book call.
   * Errors may be keyed by "Item:N" (object) or be a flat array; the events endpoint
   * puts the gate message under `error` ("already fully booked", lead time, ...), the
   * appointments shape uses `message`. Undefined for validation-shaped items.
   */
  private firstBookingErrorMessage(error: HttpErrorResponse): string | undefined {
    const errors = error?.error?.errors;
    const firstError = errors?.[0] ?? (errors ? Object.values(errors)[0] : undefined);
    return firstError?.message || firstError?.error;
  }

  private processBooking(bookingItems: EventBookItem[]): void {
    this.adyenResumeService.markBookingSent(this.partner?.id, this.adyenPaymentDetails?.session_id);
    this.formsService
      .bookEvents(bookingItems)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: appointment => {
          this.adyenResumeService.releaseIfBookingSent(this.partner?.id);
          this.showStripeRedirectionLoader = false;
          if (appointment.success) {
            // send booking success to event to parent window
            this.googleAnalyticsService.emitEventToParent(WINDOW_LISTENER_CONSTANTS.EVENT_BOOKING_DONE, appointment?.notificationHelperArray);
            this.googleAnalyticsService.emitBookingEvent(GTM_EVENTS.EVENT_BOOKING_STEP_SUCCESS);
            this.googleAnalyticsService.generateSessionUuid();

            // Check if waiting list message isn't active then check for any booking is not success because of
            // event limit is reached
            if (!this.isWaitingListBookingMessageEnabled) {
              this.showBookingLimitReachedMessageOnFinalPage = bookingItems?.length !== appointment?.notificationHelperArray?.length;
            }

            this.finalPage = true;
            if (appointment?.notificationHelperArray?.length > 0) {
              for (const item of appointment?.notificationHelperArray) {
                if (item?.customerRegistration?.uuid) {
                  this.successFullBookings.push({
                    landingPageLink: environment?.calioDashboardBaseUrl + '/events/' + item?.customerRegistration?.uuid,
                    bookingUUID: item?.customerRegistration?.uuid,
                  });
                }
              }
            }

            // clear cart from ls and object
            this.localStorageService.removeItem(this.STATE_LS_NAME, () => { });
            this.localStorageService.removeItem(this.CART_LS_NAME, () => { });
            this.localStorageService.removeItem(this.BOOKED_ITEMS_LS_NAME, () => { });

            Object.assign(this.eventState, {
              eventId: null,
              date: null,
              additional_guests_enabled: 0,
              number_allowed_additional_guests: 0,
              is_multi_day: 0,
              has_one_slot: 0,
              slots: [],
              day: 0,
              foundedSlot: null,
            });
            this.cart = [];
            this.additionalGuests = [];
            this.anonymousGuestCount = 0;
          } else {
            // Redirect to final page with error message
            this.finalPage = false;
          }
          this.priceAfterCouponReduction = undefined;
          this.cartVat.set({ byRate: [], totalCents: 0 });
          this.stripePaymentMethodId = undefined;
          this.paypalOrderDetail = undefined;

          if (this.isWhitelabel()) {
            if (
              this.partnerService.hasFeature('is_external_success_page_forwarding_enabled') &&
              this.partner.is_redirect_after_booking_enabled &&
              this.partner.successful_booking_redirect_url &&
              this.partner.redirect_after_booking_action === this.bookingRedirectionViews.BOOKING_SUMMARY_VIEW &&
              !this.globals.isInternal
            ) {
              this.helperService.redirectToUrl(this.partner.successful_booking_redirect_url);
            }
          }

          this.booked = true;
          this.disableBookingButton = false;
          this.bookButtonLoader = false;
          this.bookingStarted = false;
          this.isInvalidCoupon = false;
          this.storageComplete = false;
          this.stripePaymentMethodId = undefined;
          this.paypalOrderDetail = undefined;
          this.saferPayPaymentDetails = undefined;
        },
        error: error => {
          // A paid booking still held by the booking lock after every resend is most likely being
          // completed: keep this tab's record that /book was sent, so a reload cannot start a
          // second payment beside it.
          const stillCompleting = isPaidBookingStillCompleting(error, !!this.adyenPaymentDetails);
          if (!stillCompleting) {
            this.adyenResumeService.releaseIfBookingSent(this.partner?.id);
          }
          LoggerService.error(error);
          this.isPaymentInProgress = false;

          this.showRefundNotice = !!(this.stripePaymentMethodId || this.paypalOrderDetail || this.saferPayPaymentDetails || this.adyenPaymentDetails);
          // A lost answer is not a refusal: the booking may exist, so no refund is promised.
          this.refundNoticeKey = refundNoticeKeyFor(error, !!this.adyenPaymentDetails);
          this.refundTransactionId = this.stripePaymentMethodId
            || this.paypalOrderDetail?.paypal_order_id
            || this.saferPayPaymentDetails?.transaction_id
            || this.adyenPaymentDetails?.session_id;
          // After /book fails the payment tokens are dead — Stripe PI is already
          // captured (refunded async by BE cron), PayPal order is consumed, Saferpay
          // auth will expire. Invalidate locally and ask the Stripe component to
          // create a fresh PI so the next click can't reuse a dead token.
          const stripeWasUsed = !!this.stripePaymentMethodId;
          const adyenWasUsed = !!this.adyenPaymentDetails;
          const paypalOrSaferpayWasUsed = !!(this.paypalOrderDetail || this.saferPayPaymentDetails);
          // A paid booking whose answer was lost (connection dropped, 504) may have gone through.
          const outcomeUnknown = (paypalOrSaferpayWasUsed || adyenWasUsed) && isBookingOutcomeUnknown(error);
          // The backend will not claim this payment session again - which does not prove a booking.
          const sessionSpent = isPaymentSessionSpent(error);
          // The backend says this payment already backs a confirmed booking: that booking exists.
          const alreadyBooked = isAlreadyBookedWithPayment(error);
          this.stripePaymentMethodId = undefined;
          this.paypalOrderDetail = undefined;
          this.saferPayPaymentDetails = undefined;
          // The Adyen session is spent too: the backend claims a session exactly once, so
          // re-sending it would only fail. Drop it; the reset below re-renders cw-adyen.
          this.adyenPaymentDetails = undefined;
          // Reset so the next storeBookingItemsEvent goes false -> true and the
          // Stripe component's @Input setter actually fires handlePayment().
          // Without this, the second Book click updates the PI but never calls
          // confirmPayment() and the loader hangs.
          this.storageComplete = false;
          if (stripeWasUsed) {
            this.paymentService.recreatePaymentIntentSubject.next();
          }
          if (stillCompleting) {
            // Check the email, and NO "Pay again": the booking is most likely going through.
            this.paymentService.bookingStillCompletingSubject.next();
          } else if (alreadyBooked) {
            // The booking exists: its notice, the payment form kept down and NO "Pay again".
            this.paymentService.paymentAlreadyBookedSubject.next();
          } else if (sessionSpent) {
            // Handled like a lost answer, in its own words: check the email, then "Pay again".
            this.paymentService.paymentSessionSpentSubject.next();
          } else if (outcomeUnknown) {
            // The booking may exist, so the payment form must NOT come back on its own: that invited
            // a second payment beside a "check your email" notice. The customer re-arms it with
            // "Pay again" once they have checked.
            this.paymentService.bookingOutcomeUnknownSubject.next();
          } else if (paypalOrSaferpayWasUsed || adyenWasUsed) {
            // Brings the PayPal button / Saferpay launcher / Adyen Drop-in back into the DOM
            // so the customer can authorize a fresh payment for the retry. For Adyen this is
            // the ONLY route: a successful payment hides the payment options, which destroys
            // cw-adyen, so nothing is left to signal. Re-rendering it mints a fresh session.
            this.paymentService.resetPaymentSuccessStateSubject.next();
          }

          if (outcomeUnknown || sessionSpent || stillCompleting || alreadyBooked) {
            // Stay on the payment step, where cw-payment-methods says the booking could not be
            // confirmed and offers "Pay again". The final page's "could not be booked, please try
            // again" is exactly the wrong advice for a booking that may exist.
            this.bookingErrorMessage = undefined;
            this.disableBookingButton = false;
            this.bookButtonLoader = false;
            this.bookingStarted = false;
            this.googleAnalyticsService.emitBookingEvent(GTM_EVENTS.EVENT_BOOKING_STEP_FAILED);
            return;
          }

          if (this.showStripeRedirectionLoader) {
            this.showStripeRedirectionLoader = false;
            this.finalPage = false;
            this.booked = false;
            this.disableBookingButton = false;
            this.bookButtonLoader = false;
            this.viewMode = EVENT_CONSTANT.SUMMARY;
            this.navigateTo(this.viewMode);
            this.bookingErrorMessage = this.firstBookingErrorMessage(error) || error?.message;

            // Wait for the alert to be rendered before scrolling
            setTimeout(() => {
              const element = getDomRoot(this.elementRef.nativeElement).getElementById('booking-error-message');
              element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 1000);
            return;
          }

          // Check for booking error message (e.g. spam prevention) and show inline
          this.bookButtonLoader = false;
          this.disableBookingButton = false;
          // The status is tested BEFORE the message is read, exactly as appointment does. The
          // API's error renderer replaces the body of every non-HttpException with Cake's own
          // 'An Internal Error Has Occurred.', so reading the message first printed developer
          // text - untranslated - straight to the customer.
          const isServerError = error?.status === HttpStatusCode.InternalServerError;
          // A keyed Adyen refusal in the customer's language; anything else as the backend put it.
          const adyenMessageKey = isServerError ? undefined : adyenErrorMessageKey(error);
          const firstErrorMessage = adyenMessageKey
            ? this.translateService.instant(adyenMessageKey)
            : (isServerError ? undefined : this.firstBookingErrorMessage(error));
          if (firstErrorMessage) {
            this.bookingErrorMessage = firstErrorMessage;
            // Capacity / lead-time refusals used to reach the final page below and count as a
            // failed step; they render inline now but are still a failed booking for the funnel.
            this.googleAnalyticsService.emitBookingEvent(GTM_EVENTS.EVENT_BOOKING_STEP_FAILED);
            return;
          }
          // Redirect to final page with error message
          this.finalPage = false;
          if (isServerError) {
            // "Booked by someone else" is a guess about a 500. After a payment it is also the wrong
            // guess to make - a refused payment check answers 500 too - so a paid booking gets the
            // final page's generic message and the refund notice beside it.
            this.bookingErrorMessage = this.showRefundNotice
              ? undefined
              : this.translateService.instant('common.noDateError');
          }
          if (error?.error?.bookingErrors) {
            const bookingErrorKey = Object.keys(error?.error?.bookingErrors)?.[0];
            if (bookingErrorKey) {
              this.bookingErrorMessage = error?.error?.bookingErrors[bookingErrorKey]?.error;
            }
          }
          this.booked = true;
          this.bookingStarted = false;
          this.priceAfterCouponReduction = undefined;
          this.cartVat.set({ byRate: [], totalCents: 0 });
          this.isInvalidCoupon = false;
          this.googleAnalyticsService.emitBookingEvent(GTM_EVENTS.EVENT_BOOKING_STEP_FAILED);
        }
      });
  }

  // Check if all prerequisites are good on order to enable book button
  bookValidated(): boolean {
    // Validate form
    if (this.eventForm.invalid) {
      LoggerService.error(this.eventForm);
      this.markFormGroupTouched(this.eventForm);
      this.widgetUtilService.focusInvalidElements(this.elementRef);
      return false;
    }
    // Validate captcha
    if (this.isFriendlyCaptchaPending()) {
      this.captchaService.friendlyCaptchaValidationRequired.emit(true);
      return false;
    }
    return this.hasBookingData();
  }

  /**
   * Adyen authorised - the one entry point for every Adyen result, so one result can start at most
   * one booking. No Book press drives the payment here: Drop-in has its own pay button and the
   * hosted page its own, so the shopper has been charged with nothing booked. Book now, exactly as a
   * press would, unless something would stop that press: then the payment stays shown as done and
   * the shopper completes the rest and presses Book.
   */
  protected onAdyenPaid(details: AdyenPaymentDetails): void {
    this.adyenPaymentInFlight.set(false);
    this.adyenPaymentDetails = details;
    if (this.canBookOnItsOwn()) {
      this.submitBooking();
    }
  }

  /** Drop-in or the hosted window really started a payment: lock the booking until it reports back. */
  protected onAdyenPaymentStarted(): void {
    this.adyenPaymentInFlight.set(true);
  }

  /** Refused, cancelled, closed without paying, or an SDK error: the booking unlocks. */
  protected onAdyenReset(): void {
    this.adyenPaymentInFlight.set(false);
    this.adyenPaymentDetails = undefined;
  }

  /**
   * Would a Book press go through now? A booking already on its way disables Book, so a result
   * reported twice cannot send a second /book.
   */
  private canBookOnItsOwn(): boolean {
    return canBookAfterAdyenPayment({
      paymentHeld: this.paymentType === this.paymentTypes.ADYEN && !!this.adyenPaymentDetails,
      bookingBlocked: this.disableBookingButton || this.bookButtonLoader || this.payAgainRequired(),
      // The event widget keeps no slot reservation.
      reservationExpired: false,
      formIncomplete: !!this.eventForm?.invalid || !this.hasBookingData(),
      captchaPending: this.isFriendlyCaptchaPending(),
    });
  }

  private isFriendlyCaptchaPending(): boolean {
    return !!this.partner?.is_captcha_feature_enabled
      && this.partner.captcha_type === CAPTCHA_TYPE.FRIENDLY
      && !this.userState?.friendly_captcha_challenge;
  }

  /** The cart and the customer data a booking needs - what bookValidated() checks past the form. */
  private hasBookingData(): boolean {
    // Validate cart
    if (!this.cart?.length) {
      return false;
    }
    // Internal widget: customer selection OR required fields present
    if (this.customerSelected) {
      return true;
    }
    if (!this.userState.forename || !this.userState.lastName) {
      return false;
    }
    if (this.partner?.needPhoneNumber && this.partner.widget_phone_required && !this.userState.phone) {
      return false;
    }
    if (this.partner?.needMobileNumber && this.partner.widget_mobile_required && !this.userState.mobile) {
      return false;
    }
    if (this.partner?.internal_widget_email_shown === 1 && this.partner?.internal_widget_email_required === 1 && !this.userState.eMail) {
      return false;
    }
    return true;
  }

  updateUserState(newUserState: UserState): void {
    this.userState = newUserState;
  }

  updateCustomerSelected(newCustomerSelected: object): void {
    this.customerSelected = newCustomerSelected;
  }

  updateAdditionalGuests(additionalGuests: GuestModel[]) {
    this.additionalGuests = additionalGuests;
  }

  onAnonymousGuestCountChange(count: number): void {
    this.anonymousGuestCount = Math.max(0, Math.trunc(count || 0));
    // Re-run coupon math because totalParticipants changed.
    if (this.coupon) {
      this.calculateCoupon();
    }
  }

  ignore(): boolean {
    return false;
  }

  verifyOnlinePaymentStatus() {
    this.partnerService.verifyOnlinePaymentStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: paymentStatus => {
          this.isEventOnlinePaymentEnabled = Number(paymentStatus.isEventOnlinePaymentEnabled);
          if (paymentStatus) {
            if (
              paymentStatus.isEventOnlinePaymentEnabled === 1 &&
              paymentStatus.isStripeEventPaymentEnabled === 1 &&
              paymentStatus.stripeEnabled
            ) {
              this.isStripeEnabled = true;
            }
            if (paymentStatus.isOnlinePaymentMandatory === 1) {
              this.isOnlinePaymentMandatory = true;
            }
            if (paymentStatus.isBexioEventPaymentEnabled === 1 && paymentStatus.bexioEnabled) {
              this.isBexioEnabled = true;
            }
            if (paymentStatus.isEventOnlinePaymentEnabled === 1 && paymentStatus.saferpayEnabled) {
              this.isSaferpayEnabled = true;
            }
            if (paymentStatus.isEventOnlinePaymentEnabled === 1 && paymentStatus.adyenEnabled) {
              this.isAdyenEnabled = true;
            }
            if (paymentStatus.isEventOnlinePaymentEnabled === 1 && paymentStatus.paypalEnabled
            ) {
              this.isPaypalEnabled = true;
              this.paypalSetting = new PayPalSettingsDbModel();
              this.paypalSetting.access_token = paymentStatus.paypalAccessToken;
              this.paypalSetting.client_id = paymentStatus.paypalClientId;
            }
            if (paymentStatus.onlinePaymentShowOnSitePaymentType === 1 && paymentStatus.isOnlinePaymentMandatory !== 1) {
              this.isStoreEnabled = true;
            }
            this.isLexOfficePaymentTypeEnabled = (paymentStatus.lexOfficeEnabled === true);
            this.stripeConnectedAccountId = paymentStatus.stripeConnectedAccountId ?? null;
            this.defaultPaymentType = paymentStatus.defaultPaymentType ?? '';
          }
          this.paymentType = undefined;
        }
      });
  }

  onChangePaymentType(event: string) {
    this.paymentType = event;
    this.stripePaymentMethodId = null;
    this.paypalOrderDetail = undefined;
    this.saferPayPaymentDetails = undefined;
    this.isPaymentInProgress = false;
    if (event === this.paymentTypes.CREDITCARD && this.isStripeEnabled && (this.priceAfterCouponReduction ?? this.totalCartPrice) > 0) {
      this.disableBookingButton = true;
      this.stripeElementLoading = true;
    } else {
      this.disableBookingButton = false;
      this.stripeElementLoading = false;
    }
  }

  // Check if all prerequisite are good on order to enable book button
  private isOnlinePaymentBookButtonValidated(): boolean {
    if (
      this.totalCartPrice === 0 ||
      this.totalCartPrice === -1 ||
      this.priceAfterCouponReduction <= 0
    ) {
      this.paymentType = this.paymentTypes.STORE;
      return true;
    } else {
      if (
        !this.isBexioEnabled &&
        !this.isStripeEnabled &&
        !this.isPaypalEnabled &&
        !this.isSaferpayEnabled &&
        !this.isAdyenEnabled &&
        !this.isLexOfficePaymentTypeEnabled
      ) {
        // TODO Add comment online payment is not active
        this.paymentType = this.paymentTypes.STORE;
        return true;
      } else {
        if (this.isOnlinePaymentMandatory) {
          if (
            (this.stripePaymentMethodId && this.paymentType === this.paymentTypes.CREDITCARD) ||
            this.paymentType === this.paymentTypes.INVOICE ||
            (this.paypalOrderDetail && this.paymentType === this.paymentTypes.PAYPAL) ||
            (this.saferPayPaymentDetails && this.paymentType === this.paymentTypes.SAFERPAY) ||
            (this.adyenPaymentDetails && this.paymentType === this.paymentTypes.ADYEN)
          ) {
            return true;
          } else {
            // TODO it should catch not valid stripe credit cards or payment type is store / null
            this.customEventService.paymentGatewayErrorEvent.emit(true);
            return false;
          }
        } else {
          if (this.paymentType === this.paymentTypes.CREDITCARD) {
            if (this.stripePaymentMethodId) {
              return true;
            } else {
              return false;
            }
          } else if (this.paymentType === this.paymentTypes.INVOICE) {
            return true;
          } else if (this.paymentType === this.paymentTypes.STORE) {
            return true;
          } else if (this.paymentType === this.paymentTypes.LEX_OFFICE_INVOICE) {
            return true;
          } else if (this.paymentType === this.paymentTypes.SAFERPAY) {
            if (this.saferPayPaymentDetails) {
              return true;
            } else {
              this.customEventService.paymentGatewayErrorEvent.emit(true);
              return false;
            }
          } else if (this.paymentType === this.paymentTypes.PAYPAL) {
            if (this.paypalOrderDetail) {
              return true;
            } else {
              this.customEventService.paymentGatewayErrorEvent.emit(true);
              return false;
            }
          } else if (this.paymentType === this.paymentTypes.ADYEN) {
            if (this.adyenPaymentDetails) {
              return true;
            } else {
              this.customEventService.paymentGatewayErrorEvent.emit(true);
              return false;
            }
          } else {
            this.customEventService.paymentGatewayErrorEvent.emit(true);
            return false;
          }
        }
      }
    }
  }

  onStripeSuccessfulEvent(method: string) {
    this.stripePaymentMethodId = method;
    this.book();
  }

  onResetStripeTokenEvent(errorMessage: any) {
    this.bookingErrorMessage = errorMessage;
    this.helperService.bookingLoader.emit(false);
    this.disableBookingButton = false;
    this.stripeElementLoading = false;
    this.bookButtonLoader = false;
    this.stripePaymentMethodId = null;
    this.storageComplete = false;
  }

  onStripElementLoadEvent(isStripeLoaded: boolean) {
    // Loaded or failed, the loading gate must release — a failed load surfaces its
    // error through resetStripeTokenEvent and must not leave the button dead.
    this.stripeElementLoading = false;
    this.disableBookingButton = false;
  }

  onPaypalSuccessfulEvent(event: PayPalOrderDetailModel) {
    this.paypalOrderDetail = event;
    this.customEventService.paypalOrderSuccessfullEvent.emit();
    this.book();
  }

  onResetPaypalEvent(event: any) {
    this.paypalOrderDetail = null;
    this.isPaymentInProgress = false;
  }

  onCancelPayment(): void {
    this.paypalOrderDetail = undefined;
    this.isPaymentInProgress = false;
    this.customEventService.resetPayPalOrderDetailEvent.emit();
  }

  onSaferpaySuccessfulEvent(paymentDetails: SaferPayPaymentDetailsDbModel) {
    this.saferPayPaymentDetails = paymentDetails;
    this.showFullscreenOverlay = false;
    this.book();
  }

  onResetSaferpayEvent(errorMessage: string): void {
    this.bookingErrorMessage = errorMessage;
    this.finalPage = false;
    this.disableBookingButton = false;
    this.bookButtonLoader = false;
    this.saferPayPaymentDetails = undefined;
    this.showFullscreenOverlay = false;
  }

  hideBookingAgainButton(): boolean {
    if (this.finalPage) {
      if (this.partner.hide_book_additional_appointment_button === 1) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }

  onCouponSuccessEvent(event: CouponDbModel) {
    this.coupon = event;
    this.couponDisableConfirmButton = false;
    this.isInvalidCoupon = false;
    this.calculateCoupon();
    // A price of 0 unmounts the Stripe element before it can report ready, so the
    // loading gate must be released here or the book button stays disabled forever.
    if (this.priceAfterCouponReduction <= 0) {
      this.disableBookingButton = false;
      this.stripeElementLoading = false;
    }
    this.paypalOrderDetail = undefined;
    this.customEventService.couponChangeEvent.emit(this.finalCouponDiscount);
  }

  calculateCoupon() {
    if (this.coupon && this.coupon.is_fixed_amount === 1) {
      this.finalCouponDiscount = Number(this.coupon.fixed_amount);
    }

    // Total participants for pricing = booker (1) + named guests + anonymous guests.
    // Anonymous guests are paid for upfront by the booker (per Q3 product decision).
    const totalParticipants = 1 + (this.additionalGuests?.length || 0) + (this.anonymousGuestCount || 0);

    if (this.coupon && this.coupon.is_percentage === 1) {
      const totalPrice = totalParticipants * Number(this.totalCartPrice);
      this.finalCouponDiscount = Number(
        ((Number(totalPrice) / 100) * this.coupon.percentage).toFixed(2)
      );
    }

    const totalPrice = totalParticipants * Number(this.totalCartPrice);
    const netAfterCoupon = Number(totalPrice) - Number(this.finalCouponDiscount);
    const vat = this.calculateCartVat(netAfterCoupon, totalParticipants);
    this.cartVat.set(vat);
    // priceAfterCouponReduction carries the GROSS amount — it is what cw-payment-methods and the
    // Stripe PaymentIntent use as the amount to charge.
    this.priceAfterCouponReduction = Number((netAfterCoupon + vat.totalCents / 100).toFixed(2));
  }

  /** Net-after-coupon (participant-multiplied) → VAT — empty/zero for `inclusive` partners. */
  private calculateCartVat(discountedNet: number, totalParticipants: number): VatResult {
    if (!isVatAddedOnTop(this.partner?.widget_vat_type)) {
      return { byRate: [], totalCents: 0 };
    }
    const lines = this.eventCartService.totalCartTaxableLines.map(line => ({
      netCents: line.netCents * totalParticipants,
      rateHundredths: line.rateHundredths,
    }));
    const discountedNetCents = Math.round(discountedNet * 100);
    const undiscountedNetCents = Math.round(totalParticipants * Number(this.totalCartPrice) * 100);
    return calculateVat(lines, discountedNetCents, undiscountedNetCents);
  }

  onCouponFailedEvent(event: {
    disableBookingButton: boolean;
    invalidCoupon: boolean;
  }) {
    this.coupon = undefined;
    this.finalCouponDiscount = 0;
    this.couponDisableConfirmButton = event.disableBookingButton;
    this.isInvalidCoupon = event.invalidCoupon;
    this.calculateCoupon();
    this.paypalOrderDetail = undefined;
  }

  setupInitialTemplates() {
    this.nextButtonTemplate = this.widgetTemplates.find(template => template.identifier === WIDGET_CONSTANTS.EVENT_WIDGET_LABELS.WIDGET_EVENT_NEXT_LABEL);
    this.nextButtonTemplate && (this.nextButtonTemplate.is_multi_language = 1);

    this.previousButtonTemplate = this.widgetTemplates.find(template => template.identifier === WIDGET_CONSTANTS.EVENT_WIDGET_LABELS.WIDGET_EVENT_PREVIOUS_LABEL);
    this.previousButtonTemplate && (this.previousButtonTemplate.is_multi_language = 1);

    this.bookButtonTemplate = this.widgetTemplates.find(template => template.identifier === WIDGET_CONSTANTS.EVENT_WIDGET_LABELS.WIDGET_EVENT_BOOK_LABEL);
    this.bookButtonTemplate && (this.bookButtonTemplate.is_multi_language = 1);

    this.dateLabelTemplate = this.widgetTemplates.find(template => template.identifier === WIDGET_CONSTANTS.EVENT_WIDGET_LABELS.WIDGET_EVENT_DATE_LABEL);
    this.dateLabelTemplate && (this.dateLabelTemplate.is_multi_language = 1);

    this.manageAppointmentNowTemplate = this.widgetTemplates.find(template => template.id === 178);
    this.manageAppointmentNowTemplate && (this.manageAppointmentNowTemplate.is_multi_language = 1);

    this.successTitleTemplate = this.widgetTemplates.find(template => template.identifier === WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_SUCCESS_TITLE_LABEL);
    this.successTitleTemplate && (this.successTitleTemplate.is_multi_language = 1);

    this.downloadBookingDetailTemplate = this.widgetTemplates.find(template => template.id === 180);
    this.downloadBookingDetailTemplate && (this.downloadBookingDetailTemplate.is_multi_language = 1);

    this.widgetSuccessPageEventBookingLimitReachedTemplate = this.widgetTemplates.find(template => template.identifier === WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_SUCCESS_PAGE_EVENT_BOOKING_LIMIT_REACHED);
    this.widgetSuccessPageEventBookingLimitReachedTemplate && (this.widgetSuccessPageEventBookingLimitReachedTemplate.is_multi_language = 1);
  }

  displayRegularData(event: EventDateDao): boolean {
    if (event.regular_booking_count < event.regular_capacity) {
      return true;
    } else {
      return false;
    }
  }

  displayWaitingData(event: EventDateDao): boolean {
    if (event.waitinglist_booking_count < event.waitinglist_capacity) {
      return true;
    } else {
      return false;
    }
  }

  // Add item to local storage without remove previous items
  addToBookedItems(lsItemsToAdd: EventCartItem[]): void {
    const timezone = this.userState.timezone || this.timezoneService.getLocalTimezone();
    this.localStorageService.getItem(
      this.BOOKED_ITEMS_LS_NAME,
      (items: EventCartItem[]) => {
        // Remove old items from local storage
        if (items && items.length) {
          const now = new Date(moment.tz(timezone).format(DATE_TIME_ISO_T_FORMAT));
          items.forEach((itm) => {
            const fullStartTime = new Date(
              itm.date.getFullYear(),
              itm.date.getMonth(),
              itm.date.getDay(),
              itm.startTime.getHours(),
              itm.startTime.getMinutes()
            );

            if (fullStartTime <= now) {
              // Exclude element from array
              const index = items.indexOf(itm);
              if (index > -1) {
                items.splice(index, 1);
              }
            }
          });
        }

        // Add new booked items
        let updatedItems: EventCartItem[] = items;

        if (updatedItems && updatedItems.length) {
          updatedItems = updatedItems.concat(lsItemsToAdd);
        } else {
          // Set first value
          updatedItems = [];
          updatedItems = updatedItems.concat(lsItemsToAdd);
        }

        this.localStorageService.setItem(
          this.BOOKED_ITEMS_LS_NAME,
          updatedItems,
          () => {
            this.bookedItems = updatedItems;
          }
        );
      }
    );
  }

  private markFormGroupTouched(form: NgForm) {
    Object.values(form.controls).forEach((control) => {
      control.markAsTouched();
    });
  }

  private scrollToEventComponent(): void {
    if (this.widgetConf?.scroll_to_element_feature_is_enabled === 1) {
      const element = getDomRoot(this.elementRef.nativeElement).getElementById('event-component');
      element?.scrollIntoView();
    }
  }

  private resetDataBeforeLoadingSummaryPage() {
    // Only reset feature flags — re-populated by verifyOnlinePaymentStatus().
    // Coupon, payment data, and cart-derived state are preserved so navigating
    // back to summary doesn't drop what the customer already applied.
    this.isStripeEnabled = false;
    this.isOnlinePaymentMandatory = false;
    this.isBexioEnabled = false;
    this.isStoreEnabled = false;
    this.isLexOfficePaymentTypeEnabled = false;
    this.isPaypalEnabled = false;
    this.isSaferpayEnabled = false;
    // Reset with its siblings: a stale true here offers Adyen on the next summary before the
    // per-booking gate answers, and every other provider flag is already cleared on this path.
    this.isAdyenEnabled = false;
    this.showRefundNotice = false;
  }

  payUsingSaferpay(): void {
    if (this.eventForm.valid) {
      this.showFullscreenOverlay = true;
      this.paymentService.initiateSaferpaySubject.next(true);
    } else {
      this.markFormGroupTouched(this.eventForm);
      this.isOnlinePaymentBookButtonValidated();
    }
  }

  payUsingStripe(): void {
    if (this.stripePaymentMethodId) {
      this.book();
      return;
    }
    if (this.eventForm.valid) {
      this.disableBookingButton = true;
      this.bookButtonLoader = true;
      this.paymentService.initiateStripeSubject.next(true);
    } else {
      this.markFormGroupTouched(this.eventForm);
      this.isOnlinePaymentBookButtonValidated();
    }
  }

  submitBooking(): void {
    if (this.adyenPaymentInFlight()) {
      // Book is locked while the payment runs; a successful payment books by itself.
      return;
    }

    if (this.bookValidated()) {
      // When a 100% coupon reduces the price to 0, switch to STORE payment
      // to avoid triggering Stripe when the payment element is hidden.
      if (this.totalCartPrice <= 0 || this.priceAfterCouponReduction <= 0) {
        this.paymentType = this.paymentTypes.STORE;
      }

      if (this.paymentType === this.paymentTypes.SAFERPAY) {
        this.payUsingSaferpay();
      } else if (this.paymentType === this.paymentTypes.CREDITCARD) {
        this.payUsingStripe();
      } else {
        this.bookingStarted = true;
        this.book();
      }
    }
  }

  closeSaferpayOverlay(): void {
    this.showFullscreenOverlay = false;
    this.onResetSaferpayEvent('');
    this.paymentService.handleSaferpayBackgroundTriggerManually.next(true);
  }

  prepareAnalyticsPayload(): EventsAnalyticsDao {
    const payload = new EventsAnalyticsDao();

    // get selected event
    if (this.eventState?.eventId) {
      payload.event_uuid = this.eventState.eventId;
    }

    // get selected event category
    if (this.cart[0]?.category) {
      payload.event_category = this.cart[0].category;
    }

    // get selected worker id
    if (this.cart[0]?.workerId) {
      payload.worker_id = this.cart[0].workerId;
    }

    // get selected event title
    if (this.cart[0]?.referenceEvent?.title) {
      payload.event_title = this.cart[0].referenceEvent.title;
    }

    return payload;
  }

  protected handelAgendaView(event = { isNext: true, isPrevious: false }): void {
    this.calendarLoaded = false;
    this.eventOnYear = [];
    this.formsService.getSlots(
      this.eventState.eventId,
      this.agendaViewSelectedDate,
      'year',
      this.partner.is_timezone_enabled ? this.effectiveDisplayTimezone : undefined
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.calendarLoaded = true)
      )
      .subscribe({
        next: eventSlots => {
          this.availableEventDatesCount = eventSlots?.length ?? 0;
          this.noFreeSlots = !eventSlots?.length;
          const timezone = this.userState.timezone || this.timezoneService.getLocalTimezone();
          for (const item of eventSlots) {
            if (item != null) {
              // Converting Utc date to local timezone date
              const startDate = item.start_utc || item.start;
              const endDate = item.end_utc || item.end;
              item.start = new Date(moment(startDate).tz(timezone).format(DATE_TIME_ISO_T_FORMAT));
              item.end = new Date(moment(endDate).tz(timezone).format(DATE_TIME_ISO_T_FORMAT));

              item?.child_slots?.forEach(child => {
                // Converting Utc date to local timezone date
                const startDate = child.start_utc || child.start;
                const endDate = child.end_utc || child.end;
                child.start = new Date(moment(startDate).tz(timezone).format(DATE_TIME_ISO_T_FORMAT));
                child.end = new Date(moment(endDate).tz(timezone).format(DATE_TIME_ISO_T_FORMAT));
              });
              this.eventOnDate.push(item);
            }
          }
          if (eventSlots?.length) {
            this.eventOnYear = eventSlots;
            return;
          }

          const isSameOrAfterMaxYear = moment(this.agendaViewSelectedDate).isSameOrAfter(this.maxAgendaViewDate, 'year');
          const isSameAsCurrentYear = moment(this.agendaViewSelectedDate).isSame(this.currentDate, 'year');

          if (event.isNext && !isSameOrAfterMaxYear || event.isPrevious && !isSameAsCurrentYear) {
            this.viewYearChanged(event);
          }
        },
        error: (err) => LoggerService.error(err.message)
      });
  }

  protected viewYearChanged(event: { isNext: boolean, isPrevious: boolean }): void {
    this.customEventService.eventCalendarIsNextPressed = event.isNext;
    this.customEventService.eventCalendarIsPreviousPressed = event.isPrevious;
    if (event.isNext) {
      this.agendaViewSelectedDate = this.utilService.dateToStr(new Date(new Date(this.agendaViewSelectedDate).getFullYear() + 1, 0, 1));
    } else if (event.isPrevious) {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const randomYear = new Date(this.agendaViewSelectedDate).getFullYear();
      if (currentYear === randomYear - 1) {
        this.agendaViewSelectedDate = this.utilService.dateToStr(currentDate);
      } else {
        this.agendaViewSelectedDate = this.utilService.dateToStr(new Date(new Date(this.agendaViewSelectedDate).getFullYear() - 1, 0, 1));
      }
    }
    this.handelAgendaView(event);
  }

  protected redirectToExternalUrl(): void {
    this.helperService.redirectToUrl(this.partner.successful_booking_redirect_url);
  }

  protected onTimezoneChange(timezone: string): void {
    this.calendarLoaded = false;
    this.userState.timezone = timezone;
    this.cart.forEach(item => this.removeFromCart(item.slotId));
    this.customEventService.refreshCalendarEvent.emit();
  }

  protected onStoreBookingItemsEvent(): void {
    // Prepare and store bookingItems when triggered from Stripe component
    if (this.paymentType === this.paymentTypes.CREDITCARD && !this.stripePaymentMethodId) {
      LoggerService.log('[locale Storage] Storing bookingItems before Stripe verification (Event booking; triggered from Stripe component)');

      const data = this.buildRedirectionSnapshot();

      this.utilService.updateLocalStorage(EVENT_CONSTANT.EVENT_STRIPE_REDIRECTION_DATA_LS, data);
      this.storageComplete = true;
    }
  }

  /** The booking as it stands, for a payment return (Stripe redirect, Adyen resume) to book as is. */
  private buildRedirectionSnapshot(): Record<string, unknown> {
    return {
      bookingItems: this.prepareBookingItems(),
      eventState: this.eventState,
      cart: this.cart,
      customFieldValues: this.customFieldValues,
      userState: this.userState,
      additionalGuests: this.additionalGuests,
      totalCartPrice: this.totalCartPrice,
      cartVat: this.cartVat(),
      totalCartDuration: this.totalCartDuration,
      coupon: this.coupon,
      finalCouponDiscount: this.finalCouponDiscount,
      priceAfterCouponReduction: this.priceAfterCouponReduction,
      isInvalidCoupon: this.isInvalidCoupon,
    };
  }

  /** Puts the booking a payment return parked back in place (Stripe redirect, Adyen resume). */
  private restoreRedirectionSnapshot(data: Record<string, any>): void {
    this.eventState = data.eventState ?? this.eventState;
    this.cart = data.cart ?? this.cart;
    this.customFieldValues = data.customFieldValues ?? this.customFieldValues;
    this.userState = data.userState ?? this.userState;
    this.additionalGuests = data.additionalGuests ?? this.additionalGuests;
    this.totalCartPrice = data.totalCartPrice ?? this.totalCartPrice;
    if (data.cartVat) {
      this.cartVat.set(data.cartVat);
    }
    this.totalCartDuration = data.totalCartDuration ?? this.totalCartDuration;
    this.coupon = data.coupon ?? this.coupon;
    this.finalCouponDiscount = data.finalCouponDiscount ?? this.finalCouponDiscount;
    this.priceAfterCouponReduction = data.priceAfterCouponReduction ?? this.priceAfterCouponReduction;
    this.isInvalidCoupon = data.isInvalidCoupon ?? this.isInvalidCoupon;
  }

  /**
   * The shopper comes back from a page an Adyen payment had to leave for (a 3DS redirect, or the
   * hosted page in this tab). Restores the booking exactly as it was parked, lets AdyenResumeService
   * finish the payment, and books once -
   * the same stored booking a Stripe redirect return books, with the Adyen handles in place of the
   * payment intent. `/book` still verifies the payment with Adyen itself.
   */
  private resumeAdyenPayment(resume: AdyenResumeReturn): void {
    const data = resume.record.snapshot.data as Record<string, any>;
    if (!Array.isArray(data?.bookingItems)) {
      this.adyenResumeService.clear(resume.record.partnerId);
      return;
    }
    this.restoreRedirectionSnapshot(data);
    this.disableBookingButton = true;
    this.bookButtonLoader = true;
    this.bookingErrorMessage = undefined;
    this.showStripeRedirectionLoader = true;
    this.paymentType = this.paymentTypes.ADYEN;

    this.adyenResumeService.complete(resume, {
      holdScope: buildAdyenHoldScope({
        widgetType: this.widgetConstant.EVENT,
        eventCart: this.cart,
      }),
      items: this.cart?.length ?? 0,
    }).then(details => {
      this.adyenPaymentDetails = details;
      this.processBooking((data.bookingItems as EventBookItem[]).map(item => (
        item.payment_type === this.paymentTypes.ADYEN
          ? { ...item, adyen_session_id: details.session_id, adyen_session_result: details.session_result }
          : item
      )));
    }, (failure: AdyenResumeFailure) => {
      // The booking stays restored on the payment step; paying again mints a fresh session unless
      // the payment is held (Pending), which cw-adyen then says itself.
      this.showStripeRedirectionLoader = false;
      this.disableBookingButton = false;
      this.bookButtonLoader = false;
      this.bookingErrorMessage = failure?.silent
        ? undefined
        : this.translateService.instant(failure?.messageKey ?? 'adyen.errors.unexpected');
    });
  }
}
